// LanguageContext.js
import React, { createContext, useState, useContext, useEffect } from "react";

// 1. Dictionnaire des traductions
const pageTranslations = {
  "en": {
    "ui": {
      "navbar": {
        "digitalCertification": "Digital certification",
        "navigation": "Navigation",
        "actions": "Actions",
        "openMenu": "Open navigation menu",
        "closeMenu": "Close navigation menu",
        "user": "User",
        "admin": "Admin",
        "client": "Client",
        "active": "Active",
        "inactive": "Inactive",
        "manageThemes": "Manage themes",
        "superAdmin": "Super Admin",
        "organizationAdmin": "Organization administrator",
        "organizationWorkspace": "Organization workspace"
      },
      "security": {
        "tooManyRequests": "Too many attempts. Please try again later.",
        "accountLocked": "The account is temporarily locked after repeated failures.",
        "deletionCodeLocked": "The deletion code is temporarily locked after repeated failures.",
        "retryAfter": "Try again in {seconds} second(s).",
        "invalidPdf": "The uploaded file is not a valid PDF.",
        "unreadablePdf": "The PDF is corrupted, encrypted or unreadable.",
        "payloadTooLarge": "The submitted data is too large.",
        "requestId": "Request reference"
      },
      "themeManager": {
        "badge": "Shared catalog",
        "title": "Manage themes and competencies",
        "subtitle": "Add, update or delete the themes, sub-themes, default durations and bilingual competencies used when creating certificates.",
        "globalNoticeTitle": "Global changes",
        "globalNotice": "Every change is immediately visible to all clients.",
        "loading": "Loading catalog…",
        "loadError": "Unable to load the catalog.",
        "requestFailed": "The operation could not be completed.",
        "addTheme": "Add a theme",
        "themeNamePlaceholder": "New theme name",
        "add": "Add",
        "searchPlaceholder": "Search for a theme or sub-theme…",
        "subThemesCount": "sub-theme(s)",
        "noTheme": "No theme found.",
        "renameTheme": "Edit theme name",
        "addSubTheme": "Add a sub-theme",
        "days": "day(s)",
        "competencies": "competency/competencies",
        "noCompetency": "No competency has been entered.",
        "edit": "Edit",
        "emptyTheme": "This theme does not contain any sub-theme yet.",
        "createFirstSubTheme": "Create the first sub-theme",
        "selectThemeHint": "Select or add a theme.",
        "close": "Close",
        "editSubTheme": "Edit sub-theme",
        "newSubTheme": "New sub-theme",
        "subThemeName": "Sub-theme name",
        "duration": "Default duration (days)",
        "competenciesLabel": "Acquired competencies",
        "competenciesHint": "Enter one competency per line.",
        "competenciesPlaceholder": "Understand the requirements…\nPlan an audit…\nIdentify gaps…",
        "cancel": "Cancel",
        "saving": "Saving…",
        "save": "Save",
        "themeCreated": "Theme added to the global catalog.",
        "themeUpdated": "Theme updated for all clients.",
        "subThemeCreated": "Sub-theme added to the global catalog.",
        "subThemeUpdated": "Sub-theme and bilingual competencies updated for all clients.",
        "deleteTheme": "Delete theme",
        "deleteSubTheme": "Delete sub-theme",
        "confirmDeleteTheme": "Delete the theme “{name}” and all of its sub-themes?",
        "confirmDeleteSubTheme": "Delete the sub-theme “{name}”?",
        "themeDeleted": "The theme and all of its sub-themes were removed from the global catalog.",
        "subThemeDeleted": "The sub-theme and its competencies were removed from the global catalog.",
        "competenciesBilingualHint": "Enter the French and English versions in the same order, one competency per line.",
        "competenciesFrench": "French",
        "competenciesEnglish": "English",
        "competenciesFrenchPlaceholder": "Understand the requirements…\nPlan an audit…\nIdentify gaps…",
        "competenciesEnglishPlaceholder": "Understand the requirements…\nPlan an audit…\nIdentify gaps…",
        "competencyTranslationsMismatch": "Each French competency must have an English version on the same line.",
        "themeDeleteFailed": "Unable to delete the theme.",
        "subThemeNotFound": "The selected sub-theme could not be found.",
        "subThemeDeleteFailed": "Unable to delete the sub-theme.",
        "authRequired": "Please sign in again.",
        "activeClientRequired": "An active client account is required.",
        "clientInactive": "Your client account is inactive.",
        "themeNameRequired": "The theme name is required.",
        "themeExists": "A theme with this name already exists.",
        "themeNotFound": "The selected theme could not be found.",
        "themeCreateFailed": "Unable to create the theme.",
        "themeUpdateFailed": "Unable to update the theme.",
        "subThemeNameRequired": "The sub-theme name is required.",
        "subThemeExists": "This sub-theme already exists in the selected theme.",
        "invalidDuration": "Duration must be between 1 and 365 days.",
        "subThemeSaveFailed": "Unable to save the sub-theme.",
        "themeManagementForbidden": "The administrator has not granted you access to theme management.",
        "themeManagementAccessCheckFailed": "Unable to check theme management access.",
        "protectedDeletion": "Protected deletion",
        "deleteThemeTitle": "Delete this theme",
        "deleteSubThemeTitle": "Delete this sub-theme",
        "confirmDeleteThemeWithCode": "The theme “{name}” and all of its sub-themes will be deleted. Enter the secret code to confirm.",
        "confirmDeleteSubThemeWithCode": "The sub-theme “{name}” and its competencies will be deleted. Enter the secret code to confirm.",
        "deleteSecretCodeLabel": "Organization administrator deletion code",
        "deleteSecretCodePlaceholder": "Enter the deletion code",
        "confirmDelete": "Confirm deletion",
        "deleting": "Deleting…",
        "invalidDeleteSecretCode": "The administrator deletion code is incorrect.",
        "deleteSecretNotConfigured": "The administrator deletion code has not been configured.",
        "serverError": "Server error. Please try again.",
      
        "deleteCompetency": "Delete competency",
        "deleteCompetencyTitle": "Delete this competency",
        "confirmDeleteCompetencyWithCode": "The competency “{name}” will be deleted. Enter the organization administrator deletion code to confirm.",
        "competencyDeleted": "The competency was deleted from the organization catalog.",
        "competencyNotFound": "The selected competency could not be found.",
        "competencyDeleteFailed": "Unable to delete the competency.",
        "deletionCodeRequired": "The administrator deletion code is required.",
        "deletionCodeNotConfigured": "The administrator deletion code has not been configured.",
        "invalidDeletionCode": "The administrator deletion code is incorrect."
      },
      "about": {
        "connectedCertification": "Connected certification",
        "trustTechnology": "Technology that supports trust between organizations and certificate holders.",
        "badge": "About CertiLink",
        "title": "A clearer and more trustworthy certificate ecosystem",
        "subtitle": "CertiLink was designed to connect issuers, participants and verifiers through a simple, traceable and professional digital experience.",
        "missionLabel": "Our mission",
        "mission": "Make certificate creation, management and verification easier while preserving the link between every credential, its recipient and its issuer.",
        "visionLabel": "Our vision",
        "vision": "Build a digital certification environment where authenticity is easy to confirm and information is easier to organize, understand and trust.",
        "principlesEyebrow": "Our principles",
        "principlesTitle": "A product designed around confidence and clarity",
        "principlesSubtitle": "Each CertiLink experience is guided by practical principles that improve both internal management and public verification.",
        "integrity": "Integrity",
        "integrityText": "Preserve reliable links between certificates, participants and organizations.",
        "simplicity": "Simplicity",
        "simplicityText": "Reduce friction with focused interfaces and clear actions.",
        "traceability": "Traceability",
        "traceabilityText": "Keep essential certificate information structured and easy to retrieve.",
        "accessibility": "Accessibility",
        "accessibilityText": "Make verification understandable and available to authorized users and the public.",
        "lifecycleEyebrow": "Certificate lifecycle",
        "lifecycleTitle": "How CertiLink connects every stage",
        "lifecycleSubtitle": "The platform structures the complete path from account approval to final certificate verification.",
        "lifecycle1": "Organization onboarding",
        "lifecycle1Text": "A client workspace is approved and configured for certification activity.",
        "lifecycle2": "Participant management",
        "lifecycle2Text": "Participant identity and professional information are organized in one record.",
        "lifecycle3": "Certificate creation",
        "lifecycle3Text": "One or more certificates are generated and linked to the correct participant.",
        "lifecycle4": "Public verification",
        "lifecycle4Text": "A reference or QR code enables a fast authenticity check.",
        "audienceEyebrow": "Who CertiLink serves",
        "audienceTitle": "Useful for every side of certification",
        "organizations": "Training organizations and issuers",
        "organizationsText": "Centralize participant records, generate certificates and follow activity through a professional workspace.",
        "admins": "Platform administrators",
        "adminsText": "Approve access, supervise clients and maintain visibility across certification operations.",
        "recipients": "Participants and verifiers",
        "recipientsText": "Access certificates that are easier to identify, share and verify through a public journey.",
        "securityEyebrow": "Trust by design",
        "securityTitle": "Security is also an organizational discipline",
        "securityText": "CertiLink combines role-based access, linked records and public verification to limit ambiguity and keep sensitive management actions within authenticated areas.",
        "security1": "Role-based access for administrators and clients",
        "security2": "Certificate records linked to a participant and issuer",
        "security3": "Public verification separated from private management",
        "security4": "Archived records preserved instead of destructive deletion",
        "ctaTitle": "Bring more clarity to your certification workflow",
        "ctaText": "Request access to CertiLink or open your workspace to continue managing your certification activity.",
        "requestAccess": "Request access",
        "openDashboard": "Open dashboard",
        "backHome": "Back to home"
      },
      "home": {
        "certificateOfAchievement": "Certificate of Achievement",
        "digitalCredential": "Digital credential",
        "certilinkWorkspace": "CertiLink Workspace",
        "heroBadge": "Trusted digital certification",
        "heroTitle": "Create, manage and verify certificates with confidence",
        "heroSubtitle": "CertiLink connects organizations, participants and certificates in one secure and easy-to-use platform.",
        "searchLabel": "Verify a certificate",
        "searchPlaceholder": "Certificate number, participant or keyword",
        "searchButton": "Verify",
        "requestAccess": "Request an account",
        "discover": "Discover CertiLink",
        "publicVerification": "Public verification",
        "publicVerificationText": "Check certificate authenticity without signing in.",
        "centralizedManagement": "Centralized management",
        "centralizedManagementText": "Manage participants and certificates from one workspace.",
        "traceability": "Clear traceability",
        "traceabilityText": "Keep every certificate connected to its participant and organization.",
        "secureAccess": "Controlled access",
        "secureAccessText": "Separate administrator, client and public verification experiences.",
        "workflowEyebrow": "A simple workflow",
        "workflowTitle": "From participant registration to instant verification",
        "workflowSubtitle": "A structured process that keeps certification operations clear and reliable.",
        "step1Title": "Create your workspace",
        "step1Text": "Request a client account and centralize the certification activity of your organization.",
        "step2Title": "Manage participants",
        "step2Text": "Add participant information, organize records and generate one or more certificates.",
        "step3Title": "Verify authenticity",
        "step3Text": "Recipients and third parties can verify a certificate using its reference or QR code.",
        "benefitsEyebrow": "Built for real operations",
        "benefitsTitle": "A professional experience for every user",
        "benefitsText": "CertiLink simplifies daily certificate management while keeping verification accessible and transparent.",
        "benefit1": "Fast certificate creation and retrieval",
        "benefit2": "Participant records enriched with professional information",
        "benefit3": "Search, filters and dashboards for better visibility",
        "benefit4": "A public verification journey designed for trust",
        "portalTitle": "One platform, three focused experiences",
        "organization": "Organizations",
        "organizationText": "Manage participants, certificates and activity in a dedicated space.",
        "participants": "Participants",
        "participantsText": "Receive certificates that remain identifiable and easy to verify.",
        "verifiers": "Verifiers",
        "verifiersText": "Confirm authenticity quickly through a public search or QR code.",
        "ctaTitle": "Ready to modernize your certification process?",
        "ctaText": "Join CertiLink and offer a more reliable, organized and professional certificate experience.",
        "openDashboard": "Open dashboard",
        "requestNow": "Request access",
        "learnMore": "Learn more",
        "demoCertificate": "Certificate preview",
        "verified": "Verified certificate",
        "identity": "Identity confirmed",
        "issuer": "Issuer linked",
        "reference": "Unique reference"
      },
      "addParticipant": {
        "backParticipants": "Back to participants",
        "description": "Save the participant's essential information to centralize their profile and link one or more certificates.",
        "benefitAccount": "Data linked to your client account",
        "benefitLegacy": "Structured identity compatible with existing profiles",
        "benefitReady": "Profile immediately available for certification",
        "newProfile": "New profile",
        "participantInfo": "Participant information",
        "requiredFields": "Fields marked with an asterisk are required.",
        "authenticatedClient": "Authenticated client",
        "identity": "Identity",
        "firstName": "First name *",
        "lastName": "Last name *",
        "email": "Email address",
        "phone": "Phone",
        "professionalInfo": "Professional information",
        "organization": "Organization",
        "jobTitle": "Job title",
        "department": "Department",
        "externalId": "External ID",
        "firstNamePlaceholder": "Ahmed",
        "lastNamePlaceholder": "Ben Ali",
        "organizationPlaceholder": "Organization",
        "jobTitlePlaceholder": "Project manager",
        "departmentPlaceholder": "Training",
        "requiredError": "First name, last name and email are required."
      },
      "adminRequests": {
        "title": "Access requests",
        "platformScope": "All organizations",
        "organizationScope": "My organization",
        "superAdminHint": "The Super Admin handles administrator requests and supervises client requests.",
        "orgAdminHint": "You only see client requests addressed to your organization.",
        "subtitle": "Click a row to view all request details.",
        "total": "Total",
        "pending": "Pending",
        "approved": "Approved",
        "rejected": "Rejected",
        "closeMessage": "Close message",
        "searchPlaceholder": "Search by name, email, organization or role…",
        "clearSearch": "Clear search",
        "sortNewest": "Newest",
        "sortOldest": "Oldest",
        "sortNameAsc": "Name A–Z",
        "sortNameDesc": "Name Z–A",
        "sortStatus": "Status priority",
        "filters": "Filters",
        "refresh": "Refresh",
        "reset": "Reset",
        "allStatuses": "All statuses",
        "allRoles": "All roles",
        "allDates": "All dates",
        "today": "Today",
        "last7Days": "Last 7 days",
        "last30Days": "Last 30 days",
        "result": "result",
        "results": "results",
        "noPagination": "No pagination · internal scrolling",
        "adjustSearch": "Change your search or reset the filters.",
        "requester": "Requester",
        "organization": "Organization",
        "date": "Date",
        "status": "Status",
        "actions": "Actions",
        "approve": "Approve",
        "reject": "Reject",
        "clickDetails": "Click to view details",
        "coordinates": "Contact details",
        "requestInformation": "Request information",
        "requestReason": "Request reason",
        "requestedRole": "Requested role",
        "createdDate": "Creation date",
        "noReason": "No reason provided.",
        "noName": "No name",
        "noOrganization": "No organization",
        "requestNoName": "Unnamed request",
        "roleOwner": "Owner",
        "roleManager": "Manager",
        "roleHr": "Human resources",
        "roleIt": "IT",
        "roleCompliance": "Compliance",
        "roleEmployee": "Employee",
        "approveError": "Unable to approve this request.",
        "rejectError": "Unable to reject this request."
      },
      "bulkCreate": {
        "loadingThemesError": "Unable to load certificate themes.",
        "missingCertificateId": "Certificate ID is missing.",
        "downloadPdfError": "Unable to download the certificate PDF.",
        "completeFields": "Complete the training, dates and CSV file.",
        "endAfterStart": "The end date must be after the start date.",
        "noGenerated": "No certificate was generated.",
        "importError": "An error occurred during the import.",
        "checkingPermissions": "Checking permissions...",
        "loadingThemes": "Loading themes...",
        "backParticipants": "Back to participants",
        "badge": "Bulk creation",
        "title": "Import and generate in bulk",
        "downloadTemplate": "Download CSV template",
        "configure": "Configure generation",
        "configureDesc": "Select the training and its period, then import participants from a CSV file.",
        "training": "Training",
        "trainingDesc": "Theme, sub-theme and certificate template",
        "mainTheme": "Main theme",
        "selectTheme": "Select a theme",
        "subTheme": "Sub-theme",
        "selectSubTheme": "Select a sub-theme",
        "certificateModel": "Certificate template",
        "cb4cDesc": "CB4C institutional template",
        "certilinkDesc": "Official CertiLink template",
        "period": "Period",
        "periodDesc": "Continuous range or specific days",
        "continuousPeriod": "Continuous period",
        "continuousPeriodDesc": "Start and end date",
        "specificDays": "Specific days",
        "specificDaysDesc": "Manual day selection",
        "startDate": "Start date",
        "endDate": "End date",
        "addTrainingDay": "Add a training day",
        "add": "Add",
        "removeDate": "Remove {date}",
        "noSelectedDay": "No day selected.",
        "csvParticipants": "CSV participants",
        "csvDesc": "Import participants to certify",
        "readyImport": "KB · ready to import",
        "chooseCsv": "Click to choose a CSV file",
        "csvColumns": "Recommended columns: firstName, lastName, email and success.",
        "generating": "Generating...",
        "launch": "Start bulk creation",
        "summary": "Summary",
        "notSelected": "Not selected",
        "notDefined": "Not defined",
        "daysCount": "{count} day(s)",
        "noFile": "No file",
        "file": "File",
        "bestPractices": "Best practices",
        "practiceOne": "Keep one row per participant.",
        "practiceTwo": "Check email addresses before importing.",
        "practiceThree": "Use the provided template to avoid column errors.",
        "csvTemplate": "CSV template",
        "createdDownloading": "{count} certificate(s) created. Automatically downloading PDFs...",
        "createdDownloaded": "{count} certificate(s) created and automatically downloaded as PDF.",
        "partialDownloaded": "{downloaded} PDF(s) downloaded out of {total}. Allow multiple downloads in your browser and retry if needed.",
        "noIdsReturned": "The certificates were created, but the backend did not return their IDs for automatic download. Also replace certifController.js."
      },
      "certifCreate": {
        "missingCertificateId": "Certificate ID is missing.",
        "downloadPdfError": "Unable to download the certificate PDF.",
        "selectTrainingDay": "Please select at least one training day.",
        "invalidEndDate": "The end date cannot be before the start date.",
        "createdDownloading": "Certificate created successfully. Downloading PDF...",
        "createdDownloaded": "Certificate created and automatically downloaded as PDF.",
        "downloadFailed": "The certificate was created, but automatic download failed: {error}",
        "createError": "Error while creating the certificate.",
        "networkError": "Network error.",
        "checking": "Checking...",
        "loadingThemes": "Loading themes...",
        "title": "Create a Certificate",
        "mainTheme": "Main Theme",
        "selectTheme": "Select a theme",
        "subTheme": "Sub-theme",
        "selectSubTheme": "Select a sub-theme",
        "modelChoice": "Template choice",
        "dateMode": "Date mode",
        "useRange": "Use start/end",
        "chooseDays": "Choose days",
        "startDate": "Start date",
        "endDate": "End date",
        "trainingDays": "Training days",
        "noDay": "No day selected yet.",
        "validatedSuccess": "Success (Validated)",
        "creating": "Creating...",
        "createButton": "Create certificate",
        "successWord": "success",
        "downloadedAutomatically": "downloaded automatically"
      },
      "certifs": {
        "backParticipants": "Back to participants",
        "certificatesOf": "Certificates of",
        "subtitle": "Click a row or card to preview the PDF.",
        "total": "in total",
        "searchPlaceholder": "Search by ID, theme or sub-theme…",
        "clearSearch": "Clear search",
        "sortNewest": "Newest",
        "sortOldest": "Oldest",
        "sortTheme": "Theme A–Z",
        "sortDuration": "Longest duration",
        "filters": "Filters",
        "listView": "List view",
        "gridView": "Grid view",
        "reset": "Reset",
        "allThemes": "All themes",
        "allResults": "All results",
        "successful": "Successful",
        "participation": "Participation",
        "allYears": "All years",
        "noFiltered": "No results with these filters",
        "adjustFilters": "Change the search or reset the filters.",
        "certificate": "Certificate",
        "training": "Training",
        "period": "Period",
        "duration": "Duration",
        "result": "Result",
        "actions": "Actions",
        "noTheme": "No theme",
        "noSubTheme": "Sub-theme not provided",
        "noIdentifier": "No identifier",
        "standardModel": "Standard template",
        "preview": "Preview",
        "download": "Download",
        "delete": "Delete",
        "close": "Close",
        "preparingPreview": "Preparing preview…",
        "deleteConfirm": "Delete certificate {identifier}?",
        "deleteError": "Unable to delete",
        "loadingError": "Loading error",
        "accessError": "You do not have access to these certificates.",
        "loadError": "Unable to load certificates.",
        "thisParticipant": "this participant",
        "daysShort": "d",
        "to": "to",
        "pdf": "PDF",
      
        "protectedDeletion": "Protected deletion",
        "deletionCodeLabel": "Administrator deletion code",
        "deletionCodePlaceholder": "Enter the deletion code",
        "confirmDelete": "Confirm deletion",
        "deleting": "Deleting…",
        "deleteCertificateTitle": "Delete this certificate",
        "cancel": "Cancel"
      },
      "clients": {
        "checking": "Checking permissions…",
        "loading": "Loading clients…",
        "title": "Client management",
        "subtitle": "Click a row to view the client's participants.",
        "total": "clients in total",
        "searchPlaceholder": "Search a client by name or email…",
        "clearSearch": "Clear search",
        "sortNameAsc": "Name A–Z",
        "sortNameDesc": "Name Z–A",
        "sortParticipantsDesc": "Most participants",
        "sortParticipantsAsc": "Fewest participants",
        "sortStatus": "Status",
        "filters": "Filters",
        "reset": "Reset",
        "allStatuses": "All statuses",
        "active": "Active",
        "inactive": "Inactive",
        "allClients": "All clients",
        "withParticipants": "With participants",
        "withoutParticipant": "Without participants",
        "noClient": "No client found",
        "adjustSearch": "Change the search or reset the filters.",
        "client": "Client",
        "contact": "Contact",
        "participants": "Participants",
        "noName": "No name",
        "activate": "Activate",
        "deactivate": "Deactivate",
        "activateClient": "Activate client",
        "deactivateClient": "Deactivate client",
        "confirmAction": "Do you want to {action} {name}'s account?",
        "loadingError": "Unable to load",
        "updateError": "Unable to update",
        "idPrefix": "ID ·",
        "clientsCount": "clients",
        "activeSingle": "active",
        "inactiveSingle": "inactive",
        "themeManagementAccess": "Theme management",
        "themeAccessAllowed": "Allowed",
        "themeAccessDenied": "Not allowed",
        "enableThemeAccess": "Grant access",
        "disableThemeAccess": "Remove access",
        "confirmEnableThemeAccess": "Allow {name} to manage the global theme catalog?",
        "confirmDisableThemeAccess": "Remove {name}’s access to theme management?",
        "themeAccessUpdateFailed": "Unable to update theme management access",
      
        "actions": "Actions",
        "delete": "Delete",
        "deleteError": "Unable to delete the client.",
        "deleteClientTitle": "Delete this client",
        "deleteClientDescription": "Enter your administrator deletion code. The current client deletion behavior will be applied.",
        "protectedDeletion": "Protected deletion",
        "deletionCodeLabel": "Your deletion code",
        "deletionCodePlaceholder": "Enter your deletion code",
        "cancel": "Cancel",
        "confirmDelete": "Confirm deletion",
        "deleting": "Deleting…",
        "close": "Close"
      },
      "admins": {
        "title": "Administrator management",
        "subtitle": "Manage organization administrators and their platform access.",
        "searchPlaceholder": "Search by name, email or organization…",
        "allOrganizations": "All organizations",
        "allStatuses": "All statuses",
        "active": "Active",
        "inactive": "Inactive",
        "administrator": "Administrator",
        "organization": "Organization",
        "permissions": "Permissions",
        "actions": "Actions",
        "activate": "Activate",
        "deactivate": "Deactivate",
        "delete": "Delete",
        "deleteTitle": "Delete this administrator",
        "deleteDescription": "Enter your Super Admin deletion code. The administrator will be logically deleted, their organization will be deactivated and all of its client accounts will be deactivated. Existing data will be preserved.",
        "deleteSuccess": "Administrator deleted. The organization and its client accounts were deactivated.",
        "deleteError": "Unable to delete the administrator.",
        "protectedDeletion": "Protected deletion",
        "superAdminDeletionCode": "Your Super Admin deletion code",
        "codePlaceholder": "Enter your deletion code",
        "cancel": "Cancel",
        "confirmDelete": "Confirm deletion",
        "deleting": "Deleting…",
        "close": "Close",
        "statusUpdateError": "Unable to update administrator status.",
        "permissionsUpdateError": "Unable to update administrator permissions.",
        "noAdmin": "No administrator found."
      },
      "dashboard": {
        "loadError": "Unable to load dashboard data.",
        "controlCenter": "Control center",
        "title": "Dashboard",
        "subtitle": "Overview of CertiLink certificates, participants, clients and requests.",
        "operational": "System operational",
        "incident": "Incident detected",
        "latencyUnavailable": "Latency unavailable",
        "refresh": "Refresh",
        "retry": "Retry",
        "certificates": "Certificates",
        "participants": "Participants",
        "clients": "Clients",
        "pendingRequests": "Pending requests",
        "created30": "{count} created in the last 30 days",
        "new30": "{count} new in 30 days",
        "activeAccounts": "{rate} of accounts are active",
        "requestsSummary": "{approved} approved · {rejected} rejected",
        "successRate": "Success rate",
        "certificatesCount": "{count} certificates",
        "dailyRate": "Daily rate",
        "perDay": "certificates / day",
        "averageDuration": "Average duration",
        "perTraining": "days / training",
        "averageClient": "Average per client",
        "perClient": "certificates / client",
        "productionEvolution": "Production trend",
        "productionSubtitle": "Number of certificates generated over the last six months",
        "on30Days": "over 30 days",
        "resultQuality": "Result quality",
        "resultDistribution": "Success and failure breakdown",
        "success": "Successes",
        "failures": "Failures",
        "activeClients": "Most active clients",
        "activeClientsSubtitle": "Ranking by certificates generated",
        "topClient": "Top client:",
        "volumeShare": "of volume",
        "domainDistribution": "Distribution by domain",
        "domainSubtitle": "Main themes represented in certificates",
        "recentActivity": "Recent activity",
        "recentSubtitle": "Latest certificates created on the platform",
        "viewParticipants": "View participants →",
        "noActivity": "No recent activity",
        "certificate": "Certificate",
        "participant": "Participant",
        "client": "Client",
        "result": "Result",
        "date": "Date",
        "successSingle": "Success",
        "failure": "Failure",
        "platformHealth": "Platform health",
        "healthSubtitle": "Essential operational indicators",
        "activeClientsMetric": "Active clients",
        "activeOutOf": "{active} active out of {total}",
        "recordedSuccess": "{count} recorded successes",
        "topConcentration": "Top client concentration",
        "topConcentrationDesc": "Share of volume held by the most active client",
        "apiService": "API service",
        "latency": "Latency",
        "certPerParticipant": "Cert. / participant",
        "generatedCertificates": "Generated certificates",
        "successOf": "Success of",
        "available": "Available",
        "unavailable": "Unavailable"
      },
      "login": {
        "secureSpace": "Secure area",
        "manageDesc": "Manage certification from a clear and professional workspace.",
        "roleDesc": "Sign in according to your role to access only the tools and data you are authorized to use.",
        "roleAccess": "Role-based access",
        "verifiableData": "Verifiable data",
        "secureSession": "Secure session",
        "authentication": "Authentication",
        "signInDesc": "Sign in to your CertiLink workspace.",
        "email": "Email address",
        "password": "Password",
        "passwordPlaceholder": "Your password",
        "hidePassword": "Hide password",
        "showPassword": "Show password",
        "adminSecret": "Personal administrator access code",
        "secretPlaceholder": "Enter your personal access code",
        "hideSecret": "Hide code",
        "showSecret": "Show code",
        "signIn": "Sign in",
        "noAccount": "Don't have an account yet?",
        "requestAccess": "Request access",
        "missingToken": "Token missing from server response.",
        "loginFailed": "Unable to sign in.",
      
        "adminAccessCode": "Personal administrator access code",
        "adminAccessCodePlaceholder": "Enter your personal access code",
        "adminAccessCodeHelp": "Each administrator uses their own access code in addition to their password."
      },
      "participants": {
        "noName": "No name",
        "notProvided": "Not provided",
        "loadError": "Unable to load participants",
        "loadingError": "Loading error",
        "requiredError": "First name, last name and email are required.",
        "updateError": "Unable to update",
        "deleteError": "Unable to delete",
        "subtitle": "Click a row to view their certificates.",
        "total": "in total",
        "searchPlaceholder": "Name, email, organization, job title…",
        "filters": "Filters",
        "sortLabel": "Sort participants",
        "sortNewest": "Newest",
        "sortOldest": "Oldest",
        "sortNameAsc": "Name A → Z",
        "sortNameDesc": "Name Z → A",
        "sortCertificates": "Most certificates",
        "reset": "Reset",
        "allClients": "All clients",
        "allOrganizations": "All organizations",
        "allDepartments": "All departments",
        "allCertificates": "All certificates",
        "withCertificate": "With certificate",
        "withoutCertificate": "Without certificate",
        "participant": "Participant",
        "contact": "Contact",
        "professional": "Professional",
        "certificates": "Certificates",
        "actions": "Actions",
        "viewDetails": "View details",
        "createCertificate": "Create certificate",
        "edit": "Edit",
        "permanentDelete": "Delete permanently",
        "details": "Details",
        "viewCertificates": "View certificates",
        "newCertificate": "New certificate",
        "delete": "Delete",
        "noParticipant": "No participant found",
        "noParticipantHint": "Try another search or reset the filters.",
        "displayed": "participant(s) displayed · Continuous scrolling",
        "fullName": "Full name",
        "externalId": "External ID",
        "phone": "Phone",
        "department": "Department",
        "createdAt": "Created on",
        "updatedAt": "Updated on",
        "mongoId": "MongoDB ID",
        "customFields": "Custom fields",
        "metadata": "Metadata",
        "editParticipant": "Edit participant",
        "firstName": "First name",
        "cancel": "Cancel",
        "organizationMissing": "Organization not provided",
        "deleteWarning": " and their {count} certificate(s)",
        "deleteConfirm": "Permanently delete {name}{warning}?\n\nThis action cannot be undone.",
        "deleteSuccess": "Participant permanently deleted. {count} certificate(s) deleted.",
        "organization": "Organization",
        "jobTitle": "Job title",
        "viewCertificatesFull": "View certificates",
        "lastName": "Last name",
      
        "protectedDeletion": "Protected deletion",
        "deletionCodeLabel": "Administrator deletion code",
        "deletionCodePlaceholder": "Enter the deletion code",
        "confirmDelete": "Confirm deletion",
        "deleting": "Deleting…",
        "close": "Close",
        "deleteParticipantTitle": "Delete this participant"
      },
      "register": {
        "min8": "At least 8 characters",
        "uppercase": "One uppercase letter",
        "lowercase": "One lowercase letter",
        "number": "One number",
        "passwordCriteria": "The password does not meet all criteria yet.",
        "checkingInvitation": "Checking your invitation...",
        "invitationVerified": "Invitation verified",
        "finalizeAccess": "Finalize your CertiLink access.",
        "invitationDesc": "Your email address comes from the secure invitation. You only need to enter your name and create a strong password.",
        "controlledLink": "Verified invitation link",
        "lockedEmail": "Email address locked",
        "activatedAfterValidation": "Access activated after validation",
        "accountCreation": "Account creation",
        "finalizeRegistration": "Finalize registration",
        "formDesc": "Complete your information to secure your access.",
        "email": "Email address",
        "verifiedUneditable": "Verified and not editable",
        "fullName": "Full name",
        "fullNamePlaceholder": "Your full name",
        "createPassword": "Create a password",
        "securePassword": "Secure password",
        "hidePassword": "Hide password",
        "showPassword": "Show password",
        "creating": "Creating account...",
        "complete": "Complete registration",
        "registrationFailed": "Registration failed",
        "serverError": "Unable to contact the registration server."
      },
      "requestAccount": {
        "roleOwner": "Owner / executive",
        "roleManager": "Manager",
        "roleHr": "Human resources",
        "roleIt": "IT / Security",
        "roleCompliance": "Compliance",
        "roleEmployee": "Employee",
        "backHome": "Back to home",
        "controlledAccess": "Controlled access",
        "title": "Request access",
        "intro": "Choose the account type. Client requests are sent to the administrator of the selected organization.",
        "accountType": "Account type",
        "clientAccount": "Client account",
        "clientHelp": "Join an existing organization",
        "adminAccount": "Administrator account",
        "adminHelp": "Request a new organization environment",
        "selectOrganization": "Organization to join",
        "loadingOrganizations": "Loading organizations...",
        "chooseOrganization": "Choose an organization",
        "clientApprovalInfo": "The administrator of this organization will receive your request.",
        "newOrganization": "New organization name",
        "step1": "Send your organization information",
        "step2": "An administrator reviews your request",
        "step3": "You receive a secure invitation",
        "privacy": "The submitted data is used only to process your access request.",
        "requestInfo": "Applicant information",
        "requiredFields": "All fields marked with an asterisk are required.",
        "sent": "Request sent",
        "newRequest": "New request",
        "fullName": "Full name",
        "fullNamePlaceholder": "Your full name",
        "email": "Email address",
        "phone": "Phone",
        "organization": "Organization name",
        "role": "Your role",
        "selectRole": "Select a role",
        "reason": "Request reason",
        "reasonPlaceholder": "Briefly describe your need and intended use of CertiLink.",
        "backLogin": "Back to login",
        "send": "Send request",
        "sending": "Sending...",
        "success": "Your request has been sent. It will be reviewed by an administrator.",
        "genericError": "An error occurred.",
        "serverUnavailable": "Server unavailable."
      },
      "searchResults": {
        "missingKeywords": "Enter an ID or a name to search.",
        "noResults": "No results found.",
        "noMatching": "No matching certificate was found.",
        "publicVerification": "Public verification",
        "title": "Verification results",
        "subtitle": "View the certificate's public information and confirm its authenticity in seconds.",
        "placeholder": "ID, participant or training",
        "searchButton": "Run search",
        "searchLabel": "Search",
        "result": "result",
        "results": "results",
        "loading": "Searching certificates...",
        "loadingDetail": "Checking information...",
        "noDisplayed": "No certificate displayed",
        "retry": "Try again",
        "verified": "Verified",
        "participant": "Participant",
        "startDate": "Start date",
        "endDate": "End date",
        "duration": "Duration",
        "download": "Download",
        "authenticity": "Authenticity confirmed",
        "singlePrefix": "The certificate found is",
        "pluralPrefix": "The {count} certificates found are",
        "verifiedLower": "verified",
        "verifiedPlural": "verified",
        "certificate": "certificate"
      },
      "verifyCertif": {
        "invalidLink": "Invalid or expired link.",
        "loading": "Verifying...",
        "backHome": "Back to home"
      }
    }
  },
  "fr": {
    "ui": {
      "navbar": {
        "digitalCertification": "Certification numérique",
        "navigation": "Navigation",
        "actions": "Actions",
        "openMenu": "Ouvrir le menu de navigation",
        "closeMenu": "Fermer le menu de navigation",
        "user": "Utilisateur",
        "admin": "Administrateur",
        "client": "Client",
        "active": "Actif",
        "inactive": "Inactif",
        "manageThemes": "Gérer les thèmes",
        "superAdmin": "Super administrateur",
        "organizationAdmin": "Administrateur d’organisation",
        "organizationWorkspace": "Espace organisation"
      },
      "security": {
        "tooManyRequests": "Trop de tentatives. Veuillez réessayer plus tard.",
        "accountLocked": "Le compte est temporairement verrouillé après plusieurs échecs.",
        "deletionCodeLocked": "Le code de suppression est temporairement verrouillé après plusieurs erreurs.",
        "retryAfter": "Réessayez dans {seconds} seconde(s).",
        "invalidPdf": "Le fichier fourni n’est pas un PDF valide.",
        "unreadablePdf": "Le PDF est corrompu, chiffré ou illisible.",
        "payloadTooLarge": "Les données envoyées sont trop volumineuses.",
        "requestId": "Référence de la requête"
      },
      "themeManager": {
        "badge": "Catalogue partagé",
        "title": "Gestion des thèmes et compétences",
        "subtitle": "Ajoutez, modifiez ou supprimez les thèmes, sous-thèmes, durées par défaut et compétences bilingues utilisés lors de la création des certificats.",
        "globalNoticeTitle": "Modifications globales",
        "globalNotice": "Chaque changement est immédiatement visible pour tous les clients.",
        "loading": "Chargement du catalogue…",
        "loadError": "Impossible de charger le catalogue.",
        "requestFailed": "L’opération n’a pas pu être effectuée.",
        "addTheme": "Ajouter un thème",
        "themeNamePlaceholder": "Nom du nouveau thème",
        "add": "Ajouter",
        "searchPlaceholder": "Rechercher un thème ou sous-thème…",
        "subThemesCount": "sous-thème(s)",
        "noTheme": "Aucun thème trouvé.",
        "renameTheme": "Modifier le nom du thème",
        "addSubTheme": "Ajouter un sous-thème",
        "days": "jour(s)",
        "competencies": "compétence(s)",
        "noCompetency": "Aucune compétence renseignée.",
        "edit": "Modifier",
        "emptyTheme": "Ce thème ne contient encore aucun sous-thème.",
        "createFirstSubTheme": "Créer le premier sous-thème",
        "selectThemeHint": "Sélectionnez ou ajoutez un thème.",
        "close": "Fermer",
        "editSubTheme": "Modifier le sous-thème",
        "newSubTheme": "Nouveau sous-thème",
        "subThemeName": "Nom du sous-thème",
        "duration": "Durée par défaut (jours)",
        "competenciesLabel": "Compétences acquises",
        "competenciesHint": "Saisissez une compétence par ligne.",
        "competenciesPlaceholder": "Comprendre les exigences…\nPlanifier un audit…\nIdentifier les écarts…",
        "cancel": "Annuler",
        "saving": "Enregistrement…",
        "save": "Enregistrer",
        "themeCreated": "Thème ajouté au catalogue global.",
        "themeUpdated": "Thème modifié pour tous les clients.",
        "subThemeCreated": "Sous-thème ajouté au catalogue global.",
        "subThemeUpdated": "Sous-thème et compétences bilingues modifiés pour tous les clients.",
        "deleteTheme": "Supprimer le thème",
        "deleteSubTheme": "Supprimer le sous-thème",
        "confirmDeleteTheme": "Supprimer le thème « {name} » et tous ses sous-thèmes ?",
        "confirmDeleteSubTheme": "Supprimer le sous-thème « {name} » ?",
        "themeDeleted": "Le thème et tous ses sous-thèmes ont été supprimés du catalogue global.",
        "subThemeDeleted": "Le sous-thème et ses compétences ont été supprimés du catalogue global.",
        "competenciesBilingualHint": "Saisissez les versions française et anglaise dans le même ordre, une compétence par ligne.",
        "competenciesFrench": "Français",
        "competenciesEnglish": "Anglais",
        "competenciesFrenchPlaceholder": "Comprendre les exigences…\nPlanifier un audit…\nIdentifier les écarts…",
        "competenciesEnglishPlaceholder": "Understand the requirements…\nPlan an audit…\nIdentify gaps…",
        "competencyTranslationsMismatch": "Chaque compétence française doit avoir une version anglaise sur la même ligne.",
        "themeDeleteFailed": "Impossible de supprimer le thème.",
        "subThemeNotFound": "Le sous-thème sélectionné est introuvable.",
        "subThemeDeleteFailed": "Impossible de supprimer le sous-thème.",
        "authRequired": "Veuillez vous reconnecter.",
        "activeClientRequired": "Un compte client actif est requis.",
        "clientInactive": "Votre compte client est inactif.",
        "themeNameRequired": "Le nom du thème est obligatoire.",
        "themeExists": "Un thème portant ce nom existe déjà.",
        "themeNotFound": "Le thème sélectionné est introuvable.",
        "themeCreateFailed": "Impossible de créer le thème.",
        "themeUpdateFailed": "Impossible de modifier le thème.",
        "subThemeNameRequired": "Le nom du sous-thème est obligatoire.",
        "subThemeExists": "Ce sous-thème existe déjà dans le thème sélectionné.",
        "invalidDuration": "La durée doit être comprise entre 1 et 365 jours.",
        "subThemeSaveFailed": "Impossible d’enregistrer le sous-thème.",
        "themeManagementForbidden": "L’administrateur ne vous a pas autorisé à gérer les thèmes.",
        "themeManagementAccessCheckFailed": "Impossible de vérifier l’accès à la gestion des thèmes.",
        "protectedDeletion": "Suppression protégée",
        "deleteThemeTitle": "Supprimer ce thème",
        "deleteSubThemeTitle": "Supprimer ce sous-thème",
        "confirmDeleteThemeWithCode": "Le thème « {name} » et tous ses sous-thèmes seront supprimés. Saisissez le code secret pour confirmer.",
        "confirmDeleteSubThemeWithCode": "Le sous-thème « {name} » et ses compétences seront supprimés. Saisissez le code secret pour confirmer.",
        "deleteSecretCodeLabel": "Code de suppression de l’administrateur de l’organisation",
        "deleteSecretCodePlaceholder": "Saisissez le code de suppression",
        "confirmDelete": "Confirmer la suppression",
        "deleting": "Suppression…",
        "invalidDeleteSecretCode": "Le code de suppression de l’administrateur est incorrect.",
        "deleteSecretNotConfigured": "Le code de suppression de l’administrateur n’est pas configuré.",
        "serverError": "Erreur serveur. Veuillez réessayer.",
      
        "deleteCompetency": "Supprimer la compétence",
        "deleteCompetencyTitle": "Supprimer cette compétence",
        "confirmDeleteCompetencyWithCode": "La compétence « {name} » sera supprimée. Saisissez le code de suppression de l’administrateur de l’organisation pour confirmer.",
        "competencyDeleted": "La compétence a été supprimée du catalogue de l’organisation.",
        "competencyNotFound": "La compétence sélectionnée est introuvable.",
        "competencyDeleteFailed": "Impossible de supprimer la compétence.",
        "deletionCodeRequired": "Le code de suppression de l’administrateur est obligatoire.",
        "deletionCodeNotConfigured": "Le code de suppression de l’administrateur n’est pas configuré.",
        "invalidDeletionCode": "Le code de suppression de l’administrateur est incorrect."
      },
      "about": {
        "connectedCertification": "Certification connectée",
        "trustTechnology": "Une technologie au service de la confiance entre organismes et bénéficiaires.",
        "badge": "À propos de CertiLink",
        "title": "Un écosystème de certificats plus clair et plus fiable",
        "subtitle": "CertiLink a été conçu pour relier les émetteurs, les participants et les vérificateurs à travers une expérience numérique simple, traçable et professionnelle.",
        "missionLabel": "Notre mission",
        "mission": "Simplifier la création, la gestion et la vérification des certificats tout en préservant le lien entre chaque document, son bénéficiaire et son organisme émetteur.",
        "visionLabel": "Notre vision",
        "vision": "Construire un environnement de certification numérique où l’authenticité se confirme facilement et où l’information est mieux organisée, comprise et digne de confiance.",
        "principlesEyebrow": "Nos principes",
        "principlesTitle": "Un produit pensé autour de la confiance et de la clarté",
        "principlesSubtitle": "Chaque expérience CertiLink repose sur des principes concrets qui améliorent la gestion interne comme la vérification publique.",
        "integrity": "Intégrité",
        "integrityText": "Préserver des liens fiables entre certificats, participants et organismes.",
        "simplicity": "Simplicité",
        "simplicityText": "Réduire les frictions avec des interfaces ciblées et des actions claires.",
        "traceability": "Traçabilité",
        "traceabilityText": "Structurer les informations essentielles et les rendre faciles à retrouver.",
        "accessibility": "Accessibilité",
        "accessibilityText": "Rendre la vérification compréhensible et accessible aux utilisateurs concernés.",
        "lifecycleEyebrow": "Cycle de vie du certificat",
        "lifecycleTitle": "Comment CertiLink relie chaque étape",
        "lifecycleSubtitle": "La plateforme structure tout le parcours, de l’approbation du compte à la vérification finale du certificat.",
        "lifecycle1": "Intégration de l’organisme",
        "lifecycle1Text": "Un espace client est approuvé et configuré pour l’activité de certification.",
        "lifecycle2": "Gestion du participant",
        "lifecycle2Text": "Son identité et ses informations professionnelles sont organisées dans une fiche unique.",
        "lifecycle3": "Création du certificat",
        "lifecycle3Text": "Un ou plusieurs certificats sont générés et reliés au bon participant.",
        "lifecycle4": "Vérification publique",
        "lifecycle4Text": "Une référence ou un QR code permet de contrôler rapidement l’authenticité.",
        "audienceEyebrow": "À qui s’adresse CertiLink",
        "audienceTitle": "Utile pour chaque acteur de la certification",
        "organizations": "Organismes de formation et émetteurs",
        "organizationsText": "Centralisez les participants, générez les certificats et suivez l’activité dans un espace professionnel.",
        "admins": "Administrateurs de la plateforme",
        "adminsText": "Approuvez les accès, supervisez les clients et gardez une visibilité sur les opérations.",
        "recipients": "Participants et vérificateurs",
        "recipientsText": "Profitez de certificats plus faciles à identifier, partager et vérifier via un parcours public.",
        "securityEyebrow": "La confiance par conception",
        "securityTitle": "La sécurité est aussi une discipline d’organisation",
        "securityText": "CertiLink combine les accès par rôle, les données reliées et la vérification publique afin de réduire les ambiguïtés et de maintenir les actions sensibles dans les espaces authentifiés.",
        "security1": "Accès distincts pour les administrateurs et les clients",
        "security2": "Certificats reliés à un participant et à un organisme",
        "security3": "Vérification publique séparée de la gestion privée",
        "security4": "Archivage des dossiers plutôt que suppression destructive",
        "ctaTitle": "Apportez plus de clarté à votre parcours de certification",
        "ctaText": "Demandez un accès à CertiLink ou ouvrez votre espace pour poursuivre la gestion de votre activité.",
        "requestAccess": "Demander un accès",
        "openDashboard": "Ouvrir le dashboard",
        "backHome": "Retour à l’accueil"
      },
      "home": {
        "certificateOfAchievement": "Certificat de réussite",
        "digitalCredential": "Titre numérique",
        "certilinkWorkspace": "Espace CertiLink",
        "heroBadge": "Certification numérique de confiance",
        "heroTitle": "Créez, gérez et vérifiez vos certificats en toute confiance",
        "heroSubtitle": "CertiLink relie les organismes, les participants et leurs certificats dans une plateforme sécurisée, claire et simple à utiliser.",
        "searchLabel": "Vérifier un certificat",
        "searchPlaceholder": "Numéro du certificat, participant ou mot-clé",
        "searchButton": "Vérifier",
        "requestAccess": "Demander un compte",
        "discover": "Découvrir CertiLink",
        "publicVerification": "Vérification publique",
        "publicVerificationText": "Contrôlez l’authenticité d’un certificat sans connexion.",
        "centralizedManagement": "Gestion centralisée",
        "centralizedManagementText": "Gérez participants et certificats depuis un espace unique.",
        "traceability": "Traçabilité claire",
        "traceabilityText": "Chaque certificat reste relié à son participant et à son organisme.",
        "secureAccess": "Accès maîtrisés",
        "secureAccessText": "Des expériences distinctes pour admin, client et vérification publique.",
        "workflowEyebrow": "Un parcours simple",
        "workflowTitle": "De l’inscription du participant à la vérification instantanée",
        "workflowSubtitle": "Un processus structuré pour rendre les opérations de certification plus claires et plus fiables.",
        "step1Title": "Créez votre espace",
        "step1Text": "Demandez un compte client et centralisez l’activité de certification de votre organisme.",
        "step2Title": "Gérez les participants",
        "step2Text": "Ajoutez leurs informations, organisez les dossiers et générez un ou plusieurs certificats.",
        "step3Title": "Vérifiez l’authenticité",
        "step3Text": "Les bénéficiaires et les tiers vérifient un certificat avec sa référence ou son QR code.",
        "benefitsEyebrow": "Pensé pour les opérations réelles",
        "benefitsTitle": "Une expérience professionnelle pour chaque utilisateur",
        "benefitsText": "CertiLink simplifie la gestion quotidienne des certificats tout en gardant la vérification accessible et transparente.",
        "benefit1": "Création et récupération rapides des certificats",
        "benefit2": "Fiches participants enrichies d’informations professionnelles",
        "benefit3": "Recherche, filtres et dashboard pour une meilleure visibilité",
        "benefit4": "Parcours public de vérification conçu pour inspirer confiance",
        "portalTitle": "Une plateforme, trois expériences ciblées",
        "organization": "Organismes",
        "organizationText": "Pilotez participants, certificats et activité depuis un espace dédié.",
        "participants": "Participants",
        "participantsText": "Recevez des certificats identifiables et simples à vérifier.",
        "verifiers": "Vérificateurs",
        "verifiersText": "Confirmez rapidement l’authenticité via la recherche publique ou le QR code.",
        "ctaTitle": "Prêt à moderniser votre processus de certification ?",
        "ctaText": "Rejoignez CertiLink et proposez une expérience plus fiable, organisée et professionnelle.",
        "openDashboard": "Ouvrir le dashboard",
        "requestNow": "Demander un accès",
        "learnMore": "En savoir plus",
        "demoCertificate": "Aperçu du certificat",
        "verified": "Certificat vérifié",
        "identity": "Identité confirmée",
        "issuer": "Émetteur associé",
        "reference": "Référence unique"
      },
      "addParticipant": {
        "backParticipants": "Retour aux participants",
        "description": "Enregistrez les informations essentielles du participant afin de centraliser son profil et de lui associer un ou plusieurs certificats.",
        "benefitAccount": "Données reliées à votre compte client",
        "benefitLegacy": "Identité structurée et compatible avec les anciens profils",
        "benefitReady": "Profil immédiatement disponible pour la certification",
        "newProfile": "Nouveau profil",
        "participantInfo": "Informations du participant",
        "requiredFields": "Les champs marqués d’un astérisque sont obligatoires.",
        "authenticatedClient": "Client authentifié",
        "identity": "Identité",
        "firstName": "Prénom *",
        "lastName": "Nom *",
        "email": "Adresse email",
        "phone": "Téléphone",
        "professionalInfo": "Informations professionnelles",
        "organization": "Organisation",
        "jobTitle": "Fonction",
        "department": "Département",
        "externalId": "Identifiant externe",
        "firstNamePlaceholder": "Ahmed",
        "lastNamePlaceholder": "Ben Ali",
        "organizationPlaceholder": "Organisation",
        "jobTitlePlaceholder": "Chef de projet",
        "departmentPlaceholder": "Formation",
        "requiredError": "Le prénom, le nom et l’email sont obligatoires."
      },
      "adminRequests": {
        "title": "Demandes d’accès",
        "platformScope": "Toutes les organisations",
        "organizationScope": "Mon organisation",
        "superAdminHint": "Le Super Admin traite les demandes Admin et supervise les demandes Client.",
        "orgAdminHint": "Vous voyez uniquement les demandes Client destinées à votre organisation.",
        "subtitle": "Cliquez sur une ligne pour consulter tous les détails de la demande.",
        "total": "Total",
        "pending": "En attente",
        "approved": "Approuvées",
        "rejected": "Rejetées",
        "closeMessage": "Fermer le message",
        "searchPlaceholder": "Rechercher par nom, email, organisation, rôle…",
        "clearSearch": "Effacer la recherche",
        "sortNewest": "Plus récentes",
        "sortOldest": "Plus anciennes",
        "sortNameAsc": "Nom A–Z",
        "sortNameDesc": "Nom Z–A",
        "sortStatus": "Statut prioritaire",
        "filters": "Filtres",
        "refresh": "Actualiser",
        "reset": "Réinitialiser",
        "allStatuses": "Tous les statuts",
        "allRoles": "Tous les rôles",
        "allDates": "Toutes les dates",
        "today": "Aujourd’hui",
        "last7Days": "7 derniers jours",
        "last30Days": "30 derniers jours",
        "result": "résultat",
        "results": "résultats",
        "noPagination": "Aucune pagination · défilement interne",
        "adjustSearch": "Modifiez votre recherche ou réinitialisez les filtres.",
        "requester": "Demandeur",
        "organization": "Organisation",
        "date": "Date",
        "status": "Statut",
        "actions": "Actions",
        "approve": "Approuver",
        "reject": "Rejeter",
        "clickDetails": "Cliquez pour voir les détails",
        "coordinates": "Coordonnées",
        "requestInformation": "Informations de la demande",
        "requestReason": "Motif de la demande",
        "requestedRole": "Rôle demandé",
        "createdDate": "Date de création",
        "noReason": "Aucun motif renseigné.",
        "noName": "Sans nom",
        "noOrganization": "Sans organisation",
        "requestNoName": "Demande sans nom",
        "roleOwner": "Propriétaire",
        "roleManager": "Manager",
        "roleHr": "Ressources humaines",
        "roleIt": "Informatique",
        "roleCompliance": "Conformité",
        "roleEmployee": "Employé",
        "approveError": "Impossible d’approuver cette demande.",
        "rejectError": "Impossible de rejeter cette demande."
      },
      "bulkCreate": {
        "loadingThemesError": "Impossible de charger les thèmes de certificats.",
        "missingCertificateId": "Identifiant du certificat manquant.",
        "downloadPdfError": "Impossible de télécharger le certificat PDF.",
        "completeFields": "Complétez la formation, les dates et le fichier CSV.",
        "endAfterStart": "La date de fin doit être postérieure à la date de début.",
        "noGenerated": "Aucun certificat n’a été généré.",
        "importError": "Une erreur est survenue pendant l'importation.",
        "checkingPermissions": "Vérification des permissions...",
        "loadingThemes": "Chargement des thèmes...",
        "backParticipants": "Retour aux participants",
        "badge": "Création multiple",
        "title": "Importer et générer en masse",
        "downloadTemplate": "Télécharger le modèle CSV",
        "configure": "Configurer la génération",
        "configureDesc": "Sélectionnez la formation et sa période, puis importez les participants depuis un fichier CSV.",
        "training": "Formation",
        "trainingDesc": "Thème, sous-thème et modèle de certificat",
        "mainTheme": "Thème principal",
        "selectTheme": "Sélectionner un thème",
        "subTheme": "Sous-thème",
        "selectSubTheme": "Sélectionner un sous-thème",
        "certificateModel": "Modèle de certificat",
        "cb4cDesc": "Modèle institutionnel CB4C",
        "certilinkDesc": "Modèle officiel CertiLink",
        "period": "Période",
        "periodDesc": "Intervalle continu ou jours spécifiques",
        "continuousPeriod": "Période continue",
        "continuousPeriodDesc": "Date de début et date de fin",
        "specificDays": "Jours spécifiques",
        "specificDaysDesc": "Sélection manuelle des journées",
        "startDate": "Date de début",
        "endDate": "Date de fin",
        "addTrainingDay": "Ajouter une journée",
        "add": "Ajouter",
        "removeDate": "Retirer {date}",
        "noSelectedDay": "Aucun jour sélectionné.",
        "csvParticipants": "Participants CSV",
        "csvDesc": "Importez les participants à certifier",
        "readyImport": "Ko · prêt à être importé",
        "chooseCsv": "Cliquez pour choisir un fichier CSV",
        "csvColumns": "Colonnes conseillées : firstName, lastName, email et success.",
        "generating": "Génération en cours...",
        "launch": "Lancer la création multiple",
        "summary": "Résumé",
        "notSelected": "Non sélectionné",
        "notDefined": "Non définie",
        "daysCount": "{count} jour(s)",
        "noFile": "Aucun fichier",
        "file": "Fichier",
        "bestPractices": "Bonnes pratiques",
        "practiceOne": "Conservez une seule ligne par participant.",
        "practiceTwo": "Vérifiez les adresses email avant l’import.",
        "practiceThree": "Utilisez le modèle fourni pour éviter les erreurs de colonnes.",
        "csvTemplate": "Modèle CSV",
        "createdDownloading": "{count} certificat(s) créé(s). Téléchargement automatique des PDF...",
        "createdDownloaded": "{count} certificat(s) créé(s) et téléchargé(s) automatiquement au format PDF.",
        "partialDownloaded": "{downloaded} PDF téléchargé(s) sur {total}. Autorisez les téléchargements multiples dans votre navigateur puis relancez si nécessaire.",
        "noIdsReturned": "Les certificats ont été créés, mais le backend n’a pas renvoyé leurs identifiants pour le téléchargement automatique. Remplacez aussi certifController.js."
      },
      "certifCreate": {
        "missingCertificateId": "Identifiant du certificat manquant.",
        "downloadPdfError": "Impossible de télécharger le certificat PDF.",
        "selectTrainingDay": "Veuillez selectionner au moins un jour de formation.",
        "invalidEndDate": "La date de fin ne peut pas être antérieure à la date de début.",
        "createdDownloading": "Certificat créé avec succès. Téléchargement du PDF...",
        "createdDownloaded": "Certificat créé et téléchargé automatiquement au format PDF.",
        "downloadFailed": "Le certificat a été créé, mais son téléchargement automatique a échoué : {error}",
        "createError": "Erreur lors de la création.",
        "networkError": "Erreur réseau.",
        "checking": "Vérification...",
        "loadingThemes": "Chargement des thèmes...",
        "title": "Créer un Certificat",
        "mainTheme": "Thème Principal",
        "selectTheme": "Sélectionnez un thème",
        "subTheme": "Sous-thème",
        "selectSubTheme": "Sélectionnez un sous-thème",
        "modelChoice": "Choix du Modèle",
        "dateMode": "Mode de dates",
        "useRange": "Utiliser start/end",
        "chooseDays": "Choisir des jours",
        "startDate": "Date de début",
        "endDate": "Date de fin",
        "trainingDays": "Jours de formation",
        "noDay": "Aucun jour selectionne pour l'instant.",
        "validatedSuccess": "Succès (Validé)",
        "creating": "Création...",
        "createButton": "Créer le certificat",
        "successWord": "succès",
        "downloadedAutomatically": "téléchargé automatiquement"
      },
      "certifs": {
        "backParticipants": "Retour aux participants",
        "certificatesOf": "Certificats de",
        "subtitle": "Cliquez sur une ligne ou une carte pour prévisualiser le PDF.",
        "total": "au total",
        "searchPlaceholder": "Rechercher par identifiant, thème ou sous-thème…",
        "clearSearch": "Effacer la recherche",
        "sortNewest": "Plus récents",
        "sortOldest": "Plus anciens",
        "sortTheme": "Thème A–Z",
        "sortDuration": "Durée décroissante",
        "filters": "Filtres",
        "listView": "Vue liste",
        "gridView": "Vue grille",
        "reset": "Réinitialiser",
        "allThemes": "Tous les thèmes",
        "allResults": "Tous les résultats",
        "successful": "Réussi",
        "participation": "Participation",
        "allYears": "Toutes les années",
        "noFiltered": "Aucun résultat avec ces filtres",
        "adjustFilters": "Modifiez la recherche ou réinitialisez les filtres.",
        "certificate": "Certificat",
        "training": "Formation",
        "period": "Période",
        "duration": "Durée",
        "result": "Résultat",
        "actions": "Actions",
        "noTheme": "Sans thème",
        "noSubTheme": "Sous-thème non renseigné",
        "noIdentifier": "Sans identifiant",
        "standardModel": "Modèle standard",
        "preview": "Prévisualiser",
        "download": "Télécharger",
        "delete": "Supprimer",
        "close": "Fermer",
        "preparingPreview": "Préparation de l’aperçu…",
        "deleteConfirm": "Supprimer le certificat {identifier} ?",
        "deleteError": "Suppression impossible",
        "loadingError": "Erreur lors du chargement",
        "accessError": "Vous n’avez pas accès à ces certificats.",
        "loadError": "Impossible de charger les certificats.",
        "thisParticipant": "ce participant",
        "daysShort": "j",
        "to": "au",
        "pdf": "PDF",
      
        "protectedDeletion": "Suppression protégée",
        "deletionCodeLabel": "Code de suppression administrateur",
        "deletionCodePlaceholder": "Saisissez le code de suppression",
        "confirmDelete": "Confirmer la suppression",
        "deleting": "Suppression…",
        "deleteCertificateTitle": "Supprimer ce certificat",
        "cancel": "Annuler"
      },
      "clients": {
        "checking": "Vérification des autorisations…",
        "loading": "Chargement des clients…",
        "title": "Gestion des clients",
        "subtitle": "Cliquez sur une ligne pour consulter les participants du client.",
        "total": "clients au total",
        "searchPlaceholder": "Rechercher un client par nom ou email…",
        "clearSearch": "Effacer la recherche",
        "sortNameAsc": "Nom A–Z",
        "sortNameDesc": "Nom Z–A",
        "sortParticipantsDesc": "Plus de participants",
        "sortParticipantsAsc": "Moins de participants",
        "sortStatus": "Statut",
        "filters": "Filtres",
        "reset": "Réinitialiser",
        "allStatuses": "Tous les statuts",
        "active": "Actifs",
        "inactive": "Inactifs",
        "allClients": "Tous les clients",
        "withParticipants": "Avec participants",
        "withoutParticipant": "Sans participant",
        "noClient": "Aucun client trouvé",
        "adjustSearch": "Modifiez la recherche ou réinitialisez les filtres.",
        "client": "Client",
        "contact": "Contact",
        "participants": "Participants",
        "noName": "Sans nom",
        "activate": "Activer",
        "deactivate": "Désactiver",
        "activateClient": "Activer le client",
        "deactivateClient": "Désactiver le client",
        "confirmAction": "Voulez-vous {action} le compte de {name} ?",
        "loadingError": "Chargement impossible",
        "updateError": "Modification impossible",
        "idPrefix": "ID ·",
        "clientsCount": "clients",
        "activeSingle": "actif",
        "inactiveSingle": "inactif",
        "themeManagementAccess": "Gestion des thèmes",
        "themeAccessAllowed": "Autorisé",
        "themeAccessDenied": "Non autorisé",
        "enableThemeAccess": "Autoriser l’accès",
        "disableThemeAccess": "Retirer l’accès",
        "confirmEnableThemeAccess": "Autoriser {name} à gérer le catalogue global des thèmes ?",
        "confirmDisableThemeAccess": "Retirer à {name} l’accès à la gestion des thèmes ?",
        "themeAccessUpdateFailed": "Impossible de modifier l’accès à la gestion des thèmes",
      
        "actions": "Actions",
        "delete": "Supprimer",
        "deleteError": "Impossible de supprimer le client.",
        "deleteClientTitle": "Supprimer ce client",
        "deleteClientDescription": "Saisissez votre code de suppression administrateur. Le comportement actuel de suppression du client sera appliqué.",
        "protectedDeletion": "Suppression protégée",
        "deletionCodeLabel": "Votre code de suppression",
        "deletionCodePlaceholder": "Saisissez votre code de suppression",
        "cancel": "Annuler",
        "confirmDelete": "Confirmer la suppression",
        "deleting": "Suppression…",
        "close": "Fermer"
      },
      "admins": {
        "title": "Gestion des administrateurs",
        "subtitle": "Gérez les administrateurs d’organisation et leurs accès à la plateforme.",
        "searchPlaceholder": "Rechercher par nom, email ou organisation…",
        "allOrganizations": "Toutes les organisations",
        "allStatuses": "Tous les statuts",
        "active": "Actif",
        "inactive": "Inactif",
        "administrator": "Administrateur",
        "organization": "Organisation",
        "permissions": "Autorisations",
        "actions": "Actions",
        "activate": "Activer",
        "deactivate": "Désactiver",
        "delete": "Supprimer",
        "deleteTitle": "Supprimer cet administrateur",
        "deleteDescription": "Saisissez votre code de suppression Super Admin. L’administrateur sera supprimé logiquement, son organisation sera désactivée et tous ses comptes clients seront désactivés. Les données existantes seront conservées.",
        "deleteSuccess": "Administrateur supprimé. L’organisation et ses comptes clients ont été désactivés.",
        "deleteError": "Impossible de supprimer l’administrateur.",
        "protectedDeletion": "Suppression protégée",
        "superAdminDeletionCode": "Votre code de suppression Super Admin",
        "codePlaceholder": "Saisissez votre code de suppression",
        "cancel": "Annuler",
        "confirmDelete": "Confirmer la suppression",
        "deleting": "Suppression…",
        "close": "Fermer",
        "statusUpdateError": "Impossible de modifier le statut de l’administrateur.",
        "permissionsUpdateError": "Impossible de modifier les autorisations.",
        "noAdmin": "Aucun administrateur trouvé."
      },
      "dashboard": {
        "loadError": "Impossible de charger les données du dashboard.",
        "controlCenter": "Centre de pilotage",
        "title": "Tableau de bord",
        "subtitle": "Vue synthétique des certificats, participants, clients et demandes de CertiLink.",
        "operational": "Système opérationnel",
        "incident": "Incident détecté",
        "latencyUnavailable": "Latence indisponible",
        "refresh": "Actualiser",
        "retry": "Réessayer",
        "certificates": "Certificats",
        "participants": "Participants",
        "clients": "Clients",
        "pendingRequests": "Demandes en attente",
        "created30": "{count} créés sur les 30 derniers jours",
        "new30": "{count} nouveaux sur 30 jours",
        "activeAccounts": "{rate} des comptes sont actifs",
        "requestsSummary": "{approved} approuvées · {rejected} rejetées",
        "successRate": "Taux de réussite",
        "certificatesCount": "{count} certificats",
        "dailyRate": "Rythme quotidien",
        "perDay": "certificats / jour",
        "averageDuration": "Durée moyenne",
        "perTraining": "jours / formation",
        "averageClient": "Moyenne par client",
        "perClient": "certificats / client",
        "productionEvolution": "Évolution de la production",
        "productionSubtitle": "Nombre de certificats générés au cours des six derniers mois",
        "on30Days": "sur 30 jours",
        "resultQuality": "Qualité des résultats",
        "resultDistribution": "Répartition réussite et échec",
        "success": "Réussites",
        "failures": "Échecs",
        "activeClients": "Clients les plus actifs",
        "activeClientsSubtitle": "Classement par nombre de certificats générés",
        "topClient": "Top client :",
        "volumeShare": "du volume",
        "domainDistribution": "Répartition par domaine",
        "domainSubtitle": "Principaux thèmes représentés dans les certificats",
        "recentActivity": "Activité récente",
        "recentSubtitle": "Derniers certificats créés sur la plateforme",
        "viewParticipants": "Voir les participants →",
        "noActivity": "Aucune activité récente",
        "certificate": "Certificat",
        "participant": "Participant",
        "client": "Client",
        "result": "Résultat",
        "date": "Date",
        "successSingle": "Réussite",
        "failure": "Échec",
        "platformHealth": "Santé de la plateforme",
        "healthSubtitle": "Indicateurs opérationnels essentiels",
        "activeClientsMetric": "Clients actifs",
        "activeOutOf": "{active} actifs sur {total}",
        "recordedSuccess": "{count} réussites enregistrées",
        "topConcentration": "Concentration du top client",
        "topConcentrationDesc": "Part du volume détenue par le client le plus actif",
        "apiService": "Service API",
        "latency": "Latence",
        "certPerParticipant": "Certif. / participant",
        "generatedCertificates": "Certificats générés",
        "successOf": "Réussite de",
        "available": "Disponible",
        "unavailable": "Indisponible"
      },
      "login": {
        "secureSpace": "Espace sécurisé",
        "manageDesc": "Gérez la certification depuis un espace clair et professionnel.",
        "roleDesc": "Connectez-vous selon votre rôle pour accéder uniquement aux outils et aux données qui vous sont autorisés.",
        "roleAccess": "Accès par rôle",
        "verifiableData": "Données vérifiables",
        "secureSession": "Session sécurisée",
        "authentication": "Authentification",
        "signInDesc": "Connectez-vous à votre espace CertiLink.",
        "email": "Adresse email",
        "password": "Mot de passe",
        "passwordPlaceholder": "Votre mot de passe",
        "hidePassword": "Masquer le mot de passe",
        "showPassword": "Afficher le mot de passe",
        "adminSecret": "Code d’accès personnel administrateur",
        "secretPlaceholder": "Saisissez votre code d’accès personnel",
        "hideSecret": "Masquer le code",
        "showSecret": "Afficher le code",
        "signIn": "Se connecter",
        "noAccount": "Vous n’avez pas encore de compte ?",
        "requestAccess": "Demander un accès",
        "missingToken": "Token absent de la réponse du serveur.",
        "loginFailed": "Connexion impossible.",
      
        "adminAccessCode": "Code d’accès personnel administrateur",
        "adminAccessCodePlaceholder": "Saisissez votre code d’accès personnel",
        "adminAccessCodeHelp": "Chaque administrateur utilise son propre code d’accès en plus de son mot de passe."
      },
      "participants": {
        "noName": "Sans nom",
        "notProvided": "Non renseigné",
        "loadError": "Impossible de charger les participants",
        "loadingError": "Erreur lors du chargement",
        "requiredError": "Le prénom, le nom et l’email sont obligatoires.",
        "updateError": "Modification impossible",
        "deleteError": "Suppression impossible",
        "subtitle": "Cliquez sur une ligne pour consulter ses certificats.",
        "total": "au total",
        "searchPlaceholder": "Nom, email, organisation, fonction…",
        "filters": "Filtres",
        "sortLabel": "Trier les participants",
        "sortNewest": "Plus récents",
        "sortOldest": "Plus anciens",
        "sortNameAsc": "Nom A → Z",
        "sortNameDesc": "Nom Z → A",
        "sortCertificates": "Plus de certificats",
        "reset": "Réinitialiser",
        "allClients": "Tous les clients",
        "allOrganizations": "Toutes les organisations",
        "allDepartments": "Tous les départements",
        "allCertificates": "Tous les certificats",
        "withCertificate": "Avec certificat",
        "withoutCertificate": "Sans certificat",
        "participant": "Participant",
        "contact": "Contact",
        "professional": "Professionnel",
        "certificates": "Certificats",
        "actions": "Actions",
        "viewDetails": "Voir les détails",
        "createCertificate": "Créer un certificat",
        "edit": "Modifier",
        "permanentDelete": "Supprimer définitivement",
        "details": "Détails",
        "viewCertificates": "Voir certificats",
        "newCertificate": "Nouveau certificat",
        "delete": "Supprimer",
        "noParticipant": "Aucun participant trouvé",
        "noParticipantHint": "Essaie une autre recherche ou réinitialise les filtres.",
        "displayed": "participant(s) affiché(s) · Défilement continu",
        "fullName": "Nom complet",
        "externalId": "Identifiant externe",
        "phone": "Téléphone",
        "department": "Département",
        "createdAt": "Créé le",
        "updatedAt": "Modifié le",
        "mongoId": "ID MongoDB",
        "customFields": "Champs personnalisés",
        "metadata": "Métadonnées",
        "editParticipant": "Modifier le participant",
        "firstName": "Prénom",
        "cancel": "Annuler",
        "organizationMissing": "Organisation non renseignée",
        "deleteWarning": " ainsi que ses {count} certificat(s)",
        "deleteConfirm": "Supprimer définitivement {name}{warning} ?\n\nCette action est irréversible.",
        "deleteSuccess": "Participant supprimé définitivement. {count} certificat(s) supprimé(s).",
        "organization": "Organisation",
        "jobTitle": "Fonction",
        "viewCertificatesFull": "Voir les certificats",
        "lastName": "Nom",
      
        "protectedDeletion": "Suppression protégée",
        "deletionCodeLabel": "Code de suppression administrateur",
        "deletionCodePlaceholder": "Saisissez le code de suppression",
        "confirmDelete": "Confirmer la suppression",
        "deleting": "Suppression…",
        "close": "Fermer",
        "deleteParticipantTitle": "Supprimer ce participant"
      },
      "register": {
        "min8": "8 caractères minimum",
        "uppercase": "Une lettre majuscule",
        "lowercase": "Une lettre minuscule",
        "number": "Un chiffre",
        "passwordCriteria": "Le mot de passe ne respecte pas encore tous les critères.",
        "checkingInvitation": "Vérification de votre invitation...",
        "invitationVerified": "Invitation vérifiée",
        "finalizeAccess": "Finalisez votre accès à CertiLink.",
        "invitationDesc": "Votre adresse email provient de l'invitation sécurisée. Il ne vous reste qu'à renseigner votre nom et créer un mot de passe robuste.",
        "controlledLink": "Lien d'invitation contrôlé",
        "lockedEmail": "Adresse email verrouillée",
        "activatedAfterValidation": "Accès activé après validation",
        "accountCreation": "Création du compte",
        "finalizeRegistration": "Finaliser l'inscription",
        "formDesc": "Complétez vos informations pour sécuriser votre accès.",
        "email": "Adresse email",
        "verifiedUneditable": "Vérifiée et non modifiable",
        "fullName": "Nom complet",
        "fullNamePlaceholder": "Votre nom complet",
        "createPassword": "Créer un mot de passe",
        "securePassword": "Mot de passe sécurisé",
        "hidePassword": "Masquer le mot de passe",
        "showPassword": "Afficher le mot de passe",
        "creating": "Création du compte...",
        "complete": "Terminer l'inscription",
        "registrationFailed": "Inscription impossible",
        "serverError": "Impossible de contacter le serveur d'inscription."
      },
      "requestAccount": {
        "roleOwner": "Propriétaire / dirigeant",
        "roleManager": "Manager",
        "roleHr": "Ressources humaines",
        "roleIt": "IT / Sécurité",
        "roleCompliance": "Conformité",
        "roleEmployee": "Employé",
        "backHome": "Retour à l’accueil",
        "controlledAccess": "Accès contrôlé",
        "title": "Demander un accès",
        "intro": "Choisissez le type de compte. Les demandes Client sont transmises à l’administrateur de l’organisation sélectionnée.",
        "accountType": "Type de compte",
        "clientAccount": "Compte Client",
        "clientHelp": "Rejoindre une organisation existante",
        "adminAccount": "Compte Administrateur",
        "adminHelp": "Demander un nouvel environnement d’organisation",
        "selectOrganization": "Organisation à rejoindre",
        "loadingOrganizations": "Chargement des organisations...",
        "chooseOrganization": "Choisir une organisation",
        "clientApprovalInfo": "L’administrateur de cette organisation recevra votre demande.",
        "newOrganization": "Nom de la nouvelle organisation",
        "step1": "Envoyez les informations de votre organisation",
        "step2": "Un administrateur vérifie votre demande",
        "step3": "Vous recevez une invitation sécurisée",
        "privacy": "Les données transmises servent uniquement à traiter votre demande d’accès.",
        "requestInfo": "Informations du demandeur",
        "requiredFields": "Tous les champs marqués d’un astérisque sont obligatoires.",
        "sent": "Demande envoyée",
        "newRequest": "Nouvelle demande",
        "fullName": "Nom complet",
        "fullNamePlaceholder": "Votre nom complet",
        "email": "Adresse email",
        "phone": "Téléphone",
        "organization": "Nom de l’organisation",
        "role": "Votre rôle",
        "selectRole": "Sélectionner un rôle",
        "reason": "Motif de la demande",
        "reasonPlaceholder": "Décrivez brièvement votre besoin et l’utilisation prévue de CertiLink.",
        "backLogin": "Retour à la connexion",
        "send": "Envoyer la demande",
        "sending": "Envoi...",
        "success": "Votre demande a bien été envoyée. Elle sera examinée par un administrateur.",
        "genericError": "Une erreur est survenue.",
        "serverUnavailable": "Serveur indisponible."
      },
      "searchResults": {
        "missingKeywords": "Saisissez un identifiant ou un nom à vérifier.",
        "noResults": "Aucun résultat trouvé.",
        "noMatching": "Aucun certificat correspondant n’a été trouvé.",
        "publicVerification": "Vérification publique",
        "title": "Résultats de vérification",
        "subtitle": "Consultez les informations publiques du certificat et confirmez son authenticité en quelques secondes.",
        "placeholder": "Identifiant, participant ou formation",
        "searchButton": "Lancer la recherche",
        "searchLabel": "Recherche",
        "result": "résultat",
        "results": "résultats",
        "loading": "Recherche des certificats...",
        "loadingDetail": "Vérification des informations en cours.",
        "noDisplayed": "Aucun certificat affiché",
        "retry": "Réessayer",
        "verified": "Vérifié",
        "participant": "Participant",
        "startDate": "Date de début",
        "endDate": "Date de fin",
        "duration": "Durée",
        "download": "Télécharger",
        "authenticity": "Authenticité confirmée",
        "singlePrefix": "Le certificat trouvé est",
        "pluralPrefix": "Les {count} certificats trouvés sont",
        "verifiedLower": "vérifié",
        "verifiedPlural": "vérifiés",
        "certificate": "certificat"
      },
      "verifyCertif": {
        "invalidLink": "Lien invalide ou expiré.",
        "loading": "Vérification...",
        "backHome": "Retour à l’accueil"
      }
    }
  }
};

const translations = {
  en: {
    ...pageTranslations.en,
    home: "Home",
    about: "About",
    participants: "Participants",
    clients: "Clients",
    requests: "Requests",
    addParticipant: "Add Participant",
    multiCreate: "Multi-Create",
    dashboard: "Dashboard",
    logout: "Logout",
    login: "Login",
    requestAccess: "Request Access",
    status: "Status",
    toggleLabel: "FR 🇫🇷",
    heroTitle: "Authenticity Certified & Verification Platform",
    heroSubtitle: "Your trusted partner for secure certificate issuance and verification in Tunisia and beyond.",
    verifyCertificate: "Verify a Certificate",
    searchPlaceholder: "Enter Certificate ID...",
    search: "Search",
    cancelVerification: "Cancel verification",
    heroFooter: "Certified training • Local experts • Tailor-made solutions",
    howItWorks: "How It Works",
    step1Title: "1. Request Access",
    step1Desc: "Submit your access request. Once approved by the admin, an email will be sent to you to finalize your registration.",
    step2Title: "2. Generate Certificates",
    approvalRequired: "Approval required.",
    step2Desc: "You can only generate certificates once approved by the administration.",
    step3Title: "3. Verify Authenticity",
    step3Scan: "Please scan the",
    qrCode: "QR code",
    step3Verify: "located on the PDF certificate to verify its authenticity instantly.",
    clickToClose: "(Click again to close)",
    step3DefaultDesc: "Validate any certificate using its unique code or by scanning its QR code through CertiLink.",
    aboutTitle: "About CertiLink",
    aboutSubtitle: "CertiLink is a modern platform designed to help organizations generate, manage, and verify authentic digital certificates with ease and security.",
    ourMission: "Our Mission",
    missionText: "Our mission is to provide a secure and transparent system that prevents fraud and ensures every certificate issued is genuine and verifiable.",
    ourVision: "Our Vision",
    visionText: "We envision a future where certificates are universally trusted, instantly verifiable, and fully protected from falsification.",
    whyChooseTitle: "Why Choose CertiLink?",
    feature1Title: "Secure & Authentic",
    feature1Text: "Every certificate is protected with unique identifiers and QR codes.",
    feature2Title: "Fast Verification",
    feature2Text: "Verify certificates instantly using codes or QR scans.",
    feature3Title: "Accessible Anywhere",
    feature3Text: "Access and validate certificates anytime, from any device.",
    feature4Title: "Trusted Platform",
    feature4Text: "Designed for organizations and participants to build trust.",
    ctaTitle: "Start Using CertiLink Today",
    ctaSubtitle: "Join CertiLink and issue authentic certificates with confidence.",
    requestNow: "Request Now",
    checkingPermissions: "Checking permissions...",
    addParticipantTitle: "➕ Add Participant",
    participantWord: "Participant",
    addedSuccessSuffix: "added successfully", // Pour construire: Participant Name added successfully
    errorAddingParticipant: "Error adding participant",
    serverError: "Server error. Please try again.",
    nameLabel: "Name *",
    namePlaceholder: "Enter participant name",
    emailLabel: "Email *",
    emailPlaceholder: "Enter participant email",
    clientLabel: "Client *",
    selectClientDefault: "-- Select Client --",
    autoAssignNote: "This participant will be automatically assigned to your account.",
    cancel: "Cancel",
    adding: "Adding...",
    addParticipantButton: "Add Participant",
    loading: "Loading...",
    fetchError: "Failed to fetch requests.",
    confirmApprove: "Approve access for", // Sera suivi du nom
    confirmReject: "Reject this request?",
    adminRequestsTitle: "Access Request Management",
    thName: "Name",
    thEmail: "Email",
    thOrg: "Organization",
    thRole: "Role",
    thDate: "Date",
    thStatus: "Status",
    thActions: "Actions",
    noRequests: "No requests found.",
    statusPending: "PENDING",
    statusApproved: "APPROVED",
    statusRejected: "REJECTED",
    details: "Details",
    approve: "Approve",
    reject: "Reject",
    completed: "Completed",
    rejectedLabel: "Rejected",
    requestDetails: "Request Details",
    close: "Close",
    labelName: "Name",
    labelEmail: "Email",
    labelPhone: "Phone",
    labelOrg: "Organization",
    labelRole: "Role",
    labelReason: "Reason",
    labelDate: "Date",
    labelStatus: "Status",
    bulkCreateTitle: "Generate Multiple Certificates Simultaneously",
    bulkCreateDesc: "Generate certificates via a CSV file",
    needFormat: "Need the format?",
    downloadTemplateDesc: "Download the pre-filled CSV template.",
    downloadTemplateBtn: "Download Template",
    mainThemeLabel: "Main Theme *",
    selectThemePlaceholder: "Select a theme",
    subThemeLabel: "Sub-theme *",
    selectSubThemePlaceholder: "Select a sub-theme",
    startDateLabel: "Start Date *",
    csvFileLabel: "Participants File (.csv) *",
    readyImport: "Ready to be imported",
    dragDropCsv: "Click or drag your CSV file here",
    csvFormatNote: "Format: name, email, success",
    processing: "Processing...",
    bulkCreateBtn: "Generate Multiple Certificates at Once",
    errorFillFields: "Please fill in all fields and add a file.",
    importSuccess: "Import successful!",
    loadingThemes: "Loading themes...",
    failedFor: "Failed for",
    createCertTitle: "Create Certificate",
    selectTheme: "Select Theme",
    selectSubTheme: "Select Sub-Theme",
    successLabel: "Success?",
    createCertBtn: "Create Certificate",
    creating: "Creating...",
    certCreatedSuccess: "✅ Certificate created successfully!",
    certCreateError: "❌ Error creating certificate",
    networkError: "❌ Network error",
    loadingCertificates: "Loading certificates...",
    accessDenied: "Access Denied",
    goToLogin: "Go to Login",
    certificatesOf: "Certificates of",
    thisParticipant: "this participant",
    noCertFound: "No certificate found for this participant.",
    colId: "ID",
    colTheme: "Theme",
    colSubTheme: "Subtheme",
    colStartDate: "Start Date",
    colEndDate: "End Date",
    colDuration: "Duration",
    colAction: "Action",
    days: "days",
    downloadPdf: "Download PDF",
    downloadFailed: "Download failed",
    errorLabel: "Error",
    clientsManagement: "Clients Management",
    loadingClients: "Loading clients...",
    loadClientsError: "Unable to load clients.",
    updateFailed: "Update failed",
    colName: "Name",
    colEmail: "Email",
    colParticipants: "Participants",
    colAccountStatus: "Account Status",
    active: "Active",
    inactive: "Inactive",
    confirmBlock: "Are you sure you want to block access for {name}?",
    confirmActivate: "Are you sure you want to activate access for {name}?",
    dashboardTitle: "Analytics Dashboard",
    dashboardSubtitle: "Global performance overview",
    kpiTotalCertifs: "Total Certificates",
    kpiTotalClients: "Total Clients",
    kpiActive: "Active",
    kpiInactive: "Inactive",
    chartCreationFlow: "Creation Flow (7 Days)",
    chartLabelGenerated: "Generated Certificates",
    chartSuccessRate: "Success Rate",
    success: "Success",
    failure: "Failure",
    chartTopClients: "Top Clients",
    certificates: "Certificates",
    noClient: "No Client",
    chartThemeDist: "Distribution by Themes",
    noData: "No Data",
    clientHealthTitle: "Client Base Health",
    clientHealthDesc: "Ratio of active vs inactive clients.",
    active: "Active",
    inactive: "Inactive",
    systemStatus: "System Status",
    systemOperational: "All services are operational.",
    uptime: "Uptime",
    apiLatency: "API Latency",
    loadingParticipants: "Loading participants...",
    confirmDeleteParticipant: "Are you sure you want to delete this participant?",
    participantDeletedSuccess: "Participant deleted successfully",
    participantsOf: "Participants of",
    allParticipants: "All Participants",
    allParticipantsLow: "all participants", // Version minuscule pour phrase
    clientViewLabel: "Client View",
    clientViewDesc: "Showing your participants only",
    adminViewLabel: "Admin View",
    showing: "Showing",
    publicViewLabel: "Public View",
    publicViewDesc: "Showing all participants",
    name: "Name",
    email: "Email",
    client: "Client",
    actions: "Actions",
    createCertificate: "Create Certificate",
    delete: "Delete",
    noParticipantsFound: "No Participants Found!",
    finalizeRegistration: "Finalize Registration",
    completeSetupSecurely: "Complete your account setup securely.",
    verifiedLocked: "Verified & Locked",
    createPassword: "Create Password",
    enterName: "Enter your name",
    chooseStrongPassword: "Choose a strong password",
    creatingAccount: "Creating Account...",
    completeRegistration: "Complete Registration",
    linkExpired: "This link has expired. Please request a new one.",
    accountCreatedSuccess: "Account created successfully! Redirecting...",
    registrationFailed: "Registration failed",
    serverConnectionError: "Server connection error.",
    errorLabel: "Error",
    requestAccess: "Request Access",
    requestAccessDesc: "Fill out this form to request an account creation.",
    fullName: "Full Name *",
    enterFullName: "Enter your full name",
    emailAddress: "Email Address *",
    enterEmail: "Enter your email",
    phoneNumber: "Phone Number *",
    organizationLabel: "Organization *",
    companyNamePlaceholder: "Company Name *",
    roleLabel: "Role *",
    selectRole: "Select your role",
    roleOwner: "Owner / CEO",
    roleManager: "Manager",
    roleHR: "HR",
    roleIT: "IT / Security",
    roleCompliance: "Compliance / Quality",
    roleEmployee: "Employee",
    reasonLabel: "Reason for request *",
    reasonPlaceholder: "Why do you want access?",
    backToLogin: "Back to Login",
    submitRequest: "Submit Request",
    requestSentSuccess: "Request sent successfully! You will receive an email once the administrator approves your account.",
    errorLabel: "Error",
    serverError: "Server error. Please try again later.",
    backToHome: "Back to Home",
    searchingCertificates: "Searching certificates...",
    noResultsFound: "No results found",
    trySearchingAgain: "Try searching again with different keywords.",
    verificationResultsTitle: "Certificate Verification Results",
    verified: "Verified",
    startDate: "Start Date",
    endDate: "End Date",
    duration: "Duration",
    days: "days",
    expiration: "Expiration",
    unknown: "Unknown",
    download: "Download",
    singleCertVerifiedPrefix: "The certificate is",
    allCertsVerifiedPrefix: "All certificates are",
    and: "and",
    authentic: "authentic",
    errorLabel: "Error",
    verifiedBy: "Verified by CertiLink",
    certificateId: "Certificate ID",
    certifiesThat: "This certificate is awarded to :",
    mainTheme: "Main Theme",
    specialization: "Specialization",
    requiredSkills: "Acquired Skills",
    noPrerequisites: "No specific prerequisites are required for this training.",
    officialDocument: "Official Digital Document",
    validCertificate: "Valid Certificate",
    notVerified: "Not Verified",
    invalidLink: "Invalid or expired link.",
    backHome: "Back to Home",
    verifying: "Verifying",
    pageNotFound: "Page Not Found",
    pageNotFoundDesc: "Oops! The page you are looking for seems to have wandered off into the digital void.",
    backHome: "Go Home",
    goBack: "Go Back",
    errorReport: "If you believe this is an error, please contact support.",
    accessDenied: "Access Denied",
    accessDeniedDesc: "Sorry, you don't have the necessary permissions to view this page.",
    login: "Login",
    loginButton: "Login",
    loginTitle: "Login",
    signIn: "Sign in to access your account",
    passwordLabel: "Password *",
    passwordPlaceholder: "Enter your password",
    typeLabel: "Account Type *",
    adminRole: "Admin",
    clientRole: "Client",
    secretCodeLabel: "Secret Code *",
    secretCodePlaceholder: "Enter secret code",
    loggingIn: "Logging in...",
    loginSuccess: "Login successful!",
    selectModelLabel: "Select a Model *",
    selectModelLabelcb4c: "CB4C Model",
    selectModelLabelcertilink: "CertiLink Model",
    endDateLabel: "End Date *",
    modify: "Modify",
    modifyDesc: "Modify the participant",
    save: "Save",
    saving: "Saving...",
    dateModeLabel: "Date Mode *",
    dateRangeOption: "Date Range",
    dateDaysOption: "Number of Days",
    trainingDaysLabel: "Training Days *",
    addDayBtn: "Add Day",
    noSelectedDaysHint: "No days selected. Please add at least one training day.",
    derivedRangeLabel: "Derived Range",
    durationLabel: "Duration",
    daysLabel: "days",
    skills: {
      "Management de la Qualité": {
        "Exigences et principes de la norme ISO 9001:2015": [
          "Understand the principles of quality management",
          "Understand the vocabulary and key concepts of ISO 9001:2015",
          "Understand & master the requirements of ISO 9001:2015",
          "Apply quality management principles in a system compliant with ISO 9001:2015",
          "Develop procedures and processes compliant with ISO 9001:2015 requirements",
          "Understand risk management principles applied to quality",
          "Implement continuous improvement actions within the organization"
        ],
        "Auditeur Interne ISO 9001:2015": [
          "Consolidate knowledge regarding ISO 9001:2015 requirements",
          "Practice audit planning (Program, Document Review, Plan, Checklists...)",
          "Understand audit techniques and auditor behavior",
          "Practice internal audit against ISO 9001:2015 & ISO 19011:2018 requirements",
          "Practice formulating audit findings"
        ],
        "Exigences et Audit Interne ISO 9001:2015": [
          "Understand the principles of quality management",
          "Understand the vocabulary and key concepts of the ISO 9001:2015 standard",
          "Understand and master the requirements of the ISO 9001:2015 standard",
          "Apply quality management principles within a system compliant with ISO 9001:2015",
          "Understand audit techniques and auditor behavior",
          "Practice internal auditing in accordance with the requirements of ISO 9001:2015 & ISO 19011:2018",
          "Practice formulating audit findings"
        ],
        "Exigences ISO 9001:2015 & Auditeur Interne ISO 19011:2026": [
          "Understand the requirements of ISO 9001:2015 and the principles of a Quality Management System",
          "Master the principles and methods of internal auditing according to ISO 19011:2026",
          "Plan and prepare an internal audit of the Quality Management System",
          "Assess compliance with ISO 9001:2015 requirements",
          "Identify gaps, nonconformities, and opportunities for improvement",
          "Write audit findings and contribute to the continual improvement of the quality system"
        ],
        "Les techniques d'audit selon l'ISO 19011:2026": [
          "Understand the auditing principles and guidelines of ISO 19011:2026",
          "Acquire audit techniques and understand the expected behavior of an auditor according to ISO 19011:2026",
          "Understand the duties of an internal auditor",
          "Understand the roles, responsibilities, and tasks of an internal auditor",
          "Master the different stages of conducting an internal audit",
          "Apply auditing methods and techniques according to ISO 19011:2026",
          "Develop the skills required to conduct an internal audit",
          "Practice internal auditing according to ISO 19011:2026"
        ]
      },
      "Management de l’Environnement": {
        "Exigences ISO 14001:2015": [
          "Understand the requirements and vocabulary of ISO 14001:2015",
          "Know the principles of environmental management and the PDCA approach",
          "Identify significant environmental aspects and impacts for the organization",
          "Apply the standard's requirements to structure and improve an EMS"
        ],
        "Auditeur Interne ISO 14001:2015": [
          "Consolidate knowledge regarding ISO 14001:2015 requirements",
          "Practice audit planning (Program, Document Review, Plan, Checklists...)",
          "Understand audit techniques and auditor behavior",
          "Practice internal audit against ISO 14001:2015 & ISO 19011:2018 requirements",
          "Practice formulating audit findings"
        ],
        "Analyse du Cycle de Vie du Produit selon les normes ISO 14040V2006 & ISO 14044V2006": [  
          "Understand the requirements of ISO 14040 and ISO 14044 standards",
          "Conduct a complete Life Cycle Assessment (LCA) applied to their activity",
          "Identify significant environmental impacts",
          "Use the results to improve environmental performance",
          "Integrate LCA into an ISO 14001 approach / ESG strategy"
        ],
        "Analyse de cycle de vie dans le contexte de l'ISO 14040:2006/Amd 1:2020 et 14044:2006 Amd 2:2020": [
          "Understand the requirements of ISO 14040 and ISO 14044",
          "Conduct a complete Life Cycle Assessment (LCA) applied to the organization's activities",
          "Identify significant environmental impacts",
          "Use the results to improve environmental performance and integrate LCA into an ISO 14001 approach or ESG strategy"
        ],
        "ISO 14001 – Intégration du Système de Management Environnemental": [
          "Understand the principles and requirements of ISO 14001:2015",
          "Master the key elements of an Environmental Management System (EMS)",
          "Identify environmental aspects and assess their associated impacts",
          "Apply the process approach and environmental risk-based thinking",
          "Implement the actions required to integrate an EMS",
          "Contribute to the continual improvement of environmental performance"
        ]
      },
      "Management de la Santé et Sécurité au Travail": {
        "Exigences ISO 45001:2018": [
          "Understand the requirements of ISO 45001:2018",
          "Understand OH&S challenges",
          "Understand the regulatory and legal framework regarding OH&S",
          "Learn hazard identification, risk assessment and control in OH&S",
          "Learn ISO 45001:2018 requirements through practical exercises"
        ],
        "Auditeur Interne ISO 45001:2018": [
          "Consolidate knowledge regarding ISO 45001:2018 requirements",
          "Understand internal auditor tasks",
          "Practice internal audit against ISO 45001:2018 requirements",
          "Acquire and understand audit techniques and auditor behavior per ISO 19011:2018"
        ],
        "Sécurité pour les correspondants sécurité": [
          "Understand the role and responsibilities of a safety representative",
          "Identify hazards and analyze workplace risks",
          "Conduct effective workplace safety observations",
          "Detect and report hazardous situations and near misses",
          "Promote a safety culture within the organization and contribute to improving HSE performance"
        ],
        "Atmosphère Explosives – ATEX": [
          "Understand the principles for preventing risks related to explosive atmospheres (ATEX)",
          "Identify ATEX zones and potential ignition sources",
          "Apply prevention and protection measures against explosions",
          "Assess explosion risks related to facilities and activities",
          "Master the applicable regulatory requirements and prevention measures",
          "Apply good safety practices when working in ATEX zones",
          "Contribute to explosion risk prevention and the improvement of facility safety"
        ],
        "Habilitation électrique": [
          "Work safely on all or part of an operating electrical installation",
          "Understand the applicable regulations under the NF C 18-510 standard",
          "Apply safety instructions related to electrical isolation, general interventions, de-energized work, and work near electrical installations",
          "Identify all relevant hazards and carry out a risk assessment",
          "Understand the safety rules for working near electrical hazards",
          "Protect oneself against electrical hazards"
        ],
        "LOTO & Permis de travail": [
          "Identify the different sources of hazardous energy",
          "Understand the risks associated with work on equipment",
          "Correctly apply Lockout/Tagout (LOTO) procedures",
          "Perform safe energy isolation and restoration",
          "Implement the appropriate work permits for high-risk activities",
          "Verify the absence of energy before starting work",
          "Correctly use lockout and tagout devices",
          "Prevent accidents caused by unexpected equipment restart",
          "Coordinate maintenance and subcontractor activities safely"
        ]
      },
      "Management de l’Énergie": {
        "Exigences ISO 50001:2018": [
          "Understand standard requirements for practical implementation",
          "Understand and learn to work with the ISO 50001 family of standards",
          "Master the concepts of the energy review",
          "Apply the requirements of the standard to structure and improve an Energy Management System (EnMS)"
        ],
        "Auditeur Interne ISO 50001:2018": [
          "Consolidate knowledge regarding ISO 50001:2018 requirements",
          "Practice internal audit planning (Program, Document Review, Plan, Checklists...)",
          "Understand audit techniques and auditor behavior",
          "Practice internal audit against ISO 50001:2018 requirements",
          "Practice formulating audit findings"
        ],
        "Responsable management de l'énergie": [
          "Understand the principles of energy management",
          "Master the requirements of ISO 50001",
          "Identify significant energy uses and opportunities for improvement",
          "Define and manage energy action plans",
          "Manage an Energy Management System (EnMS)",
          "Define and implement improvement actions to optimize energy consumption and costs"
        ]
      },
      "Système de Management Intégré": {
        "Auditeur Interne SMI Energie et Environnement (ISO 50001 & ISO 14001)": [
          "Strengthening knowledge related to the requirements of ISO 50001:2018 and ISO 14001:2015",
          "Mastering audit techniques and auditor behavior",
          "Ability to prepare a complete and relevant audit checklist",
          "Writing audit findings in a clear and accurate manner"
        ],
        "Exigences d’un Système de Management Intégré : Qualité, Environnement et Sécurité/Santé au Travail ISO 9001 :2015 / ISO 14001 :2015 / ISO 45001 :2018": [
          "Understand the requirements of ISO 9001:2015, ISO 14001:2015, and ISO 45001:2018",
          "Interpret the requirements of an Integrated Management System (IMS)",
          "Master the process approach and risk-based thinking",
          "Identify the key requirements for implementing an Integrated Management System",
          "Apply the standards' requirements to the organization's context",
          "Contribute to the continual improvement of Integrated Management System performance"
        ],
        "Auditeur Interne d’un Système de Management Intégré : Qualité, Environnement et Sécurité/Santé au Travail ISO 9001 :2015 / ISO 14001 :2015 / ISO 45001 :2018": [
          "Consolidate knowledge of ISO 9001:2015, ISO 14001:2015, and ISO 45001:2018 requirements",
          "Understand the principles and techniques of auditing an Integrated Management System",
          "Understand the internal auditor's duties according to ISO 19011:2026",
          "Plan and prepare an internal audit according to ISO 19011:2026 guidelines",
          "Understand audit techniques and auditor behavior in relation to ISO 9001:2015, ISO 14001:2015, and ISO 45001:2018",
          "Conduct an internal audit of ISO 9001, ISO 14001, and ISO 45001 management systems",
          "Identify nonconformities, gaps, and opportunities for improvement"
        ],
        "Exigences d’un Système de Management Intégré : Qualité, Environnement, Energie & et Sécurité/Santé au Travail ISO 9001:2015 / ISO 14001:2015 / ISO 50001:2018 / ISO 45001:2018": [
          "Understand the requirements of ISO 9001:2015, ISO 14001:2015, ISO 45001:2018, and ISO 50001:2018",
          "Interpret the requirements of an Integrated Management System (IMS)",
          "Master the process approach and risk-based thinking",
          "Identify the key requirements for implementing an Integrated Management System",
          "Apply the standards' requirements to the organization's context",
          "Contribute to the continual improvement of Integrated Management System performance"
        ],
        "Auditeur Interne d’un Système de Management Intégré : Qualité, Environnement, Energie et Sécurité/Santé au Travail ISO 9001 :2015 / ISO 14001 :2015 / ISO 45001 :2018 / ISO 50001:2018": [
          "Consolidate knowledge of ISO 9001:2015, ISO 14001:2015, ISO 45001:2018, and ISO 50001:2018 requirements",
          "Understand the principles and techniques of auditing an Integrated Management System",
          "Understand the internal auditor's duties according to ISO 19011:2026",
          "Plan and prepare an internal audit according to ISO 19011:2026 guidelines",
          "Understand audit techniques and auditor behavior in relation to ISO 9001:2015, ISO 14001:2015, ISO 45001:2018, and ISO 50001:2018",
          "Conduct an internal audit of ISO 9001:2015, ISO 14001:2015, ISO 45001:2018, and ISO 50001:2018 management systems",
          "Identify nonconformities, gaps, and opportunities for improvement"
        ],
        "Auditeur Interne d’un Système de Management Intégré Environnement et Sécurité/Santé au Travail ISO 14001 :2015 / ISO 45001 :2018": [
          "Consolidate knowledge of ISO 14001:2015 and ISO 45001:2018 requirements",
          "Understand the principles and techniques of auditing an Integrated Management System",
          "Understand the internal auditor's duties according to ISO 19011:2026",
          "Plan and prepare an internal audit according to ISO 19011:2026 guidelines",
          "Understand audit techniques and auditor behavior in relation to ISO 14001:2015 and ISO 45001:2018",
          "Conduct an internal audit of ISO 14001:2015 and ISO 45001:2018 management systems",
          "Identify nonconformities, gaps, and opportunities for improvement"
        ]
      },
      "Management de la Sécurité de l’Information": {
        "Exigences et principes de la norme ISO 27001:2022": [
          "Understand the principles and concepts of an Information Security Management System (ISMS)",
          "Master the requirements of ISO/IEC 27001:2022",
          "Interpret the standard's requirements in an organizational context",
          "Identify the applicable requirements for implementing an ISMS",
          "Contribute to the implementation, maintenance, and continual improvement of an ISMS compliant with ISO/IEC 27001:2022"
        ],
        "Auditeur Interne ISO 27001:2022": [
          "Consolidate knowledge of ISO 27001:2022 requirements",
          "Plan an internal audit in accordance with ISO/IEC 27001:2022 requirements",
          "Practice audit planning (program, document review, audit plan, checklists, etc.)",
          "Understand audit techniques and auditor behavior",
          "Conduct internal audits against ISO 27001:2022 and ISO 19011:2026 requirements",
          "Identify nonconformities, gaps, and opportunities for improvement",
          "Practice formulating audit findings",
          "Master the regulatory requirements applicable to information systems security",
          "Identify compliance obligations related to information systems",
          "Plan and prepare an information systems audit in accordance with Tunisian regulations",
          "Assess an information system's level of compliance with regulatory requirements",
          "Identify risks, vulnerabilities, and nonconformities affecting information systems security",
          "Analyze the effectiveness of the implemented security measures"
        ],
        "Audit des Systèmes de l’Information selon la Règlementation Tunisienne": [
          "Understand the requirements of Tunisian regulations applicable to information systems auditing.",
          "Master the principles, methods, and stages of information systems auditing.",
          "Assess the compliance of an information system with Tunisian regulatory requirements.",
          "Identify risks, vulnerabilities, and compliance gaps in information systems.",
          "Apply auditing techniques to evaluate the effectiveness of implemented security measures.",
          "Formulate audit findings and recommendations to strengthen the compliance and security of information systems."
        ]
      },
    }
  },
  fr: {
    ...pageTranslations.fr,
    home: "Accueil",
    about: "À propos",
    participants: "Participants",
    clients: "Clients",
    requests: "Demandes",
    addParticipant: "Ajouter Participant",
    multiCreate: "Création Multiple",
    dashboard: "Tableau de bord",
    logout: "Déconnexion",
    login: "Connexion",
    requestAccess: "Demander accès",
    status: "Statut",
    toggleLabel: "EN 🇬🇧",
    heroTitle: "Plateforme de Certification et de Vérification",
    heroSubtitle: "Votre partenaire de confiance pour l'émission et la vérification sécurisée de certificats en Tunisie et au-delà.",
    verifyCertificate: "Vérifier un certificat",
    searchPlaceholder: "Entrez l'ID du certificat...",
    search: "Rechercher",
    cancelVerification: "Annuler la vérification",
    heroFooter: "Formation certifiée • Experts locaux • Solutions sur mesure",
    howItWorks: "Comment ça marche",
    step1Title: "1. Demander l'accès",
    step1Desc: "Soumettez votre demande. Une fois approuvée par l'admin, un email vous sera envoyé pour finaliser votre inscription.",
    step2Title: "2. Générer des certificats",
    approvalRequired: "Approbation requise.",
    step2Desc: "Vous ne pouvez générer des certificats qu'une fois approuvé par l'administration.",
    step3Title: "3. Vérifier l'authenticité",
    step3Scan: "Veuillez scanner le",
    qrCode: "code QR",
    step3Verify: "situé sur le certificat PDF pour vérifier son authenticité instantanément.",
    clickToClose: "(Cliquez à nouveau pour fermer)",
    step3DefaultDesc: "Validez n'importe quel certificat en utilisant son code unique ou en scannant son QR code via CertiLink.",
    aboutTitle: "À propos de CertiLink",
    aboutSubtitle: "CertiLink est une plateforme moderne conçue pour aider les organisations à générer, gérer et vérifier des certificats numériques authentiques avec facilité et sécurité.",
    ourMission: "Notre Mission",
    missionText: "Notre mission est de fournir un système sécurisé et transparent qui empêche la fraude et garantit que chaque certificat émis est authentique et vérifiable.",
    ourVision: "Notre Vision",
    visionText: "Nous envisageons un avenir où les certificats sont universellement reconnus, instantanément vérifiables et entièrement protégés contre la falsification.",
    whyChooseTitle: "Pourquoi choisir CertiLink ?",
    feature1Title: "Sécurisé & Authentique",
    feature1Text: "Chaque certificat est protégé par des identifiants uniques et des codes QR.",
    feature2Title: "Vérification Rapide",
    feature2Text: "Vérifiez les certificats instantanément à l'aide de codes ou de scans QR.",
    feature3Title: "Accessible Partout",
    feature3Text: "Accédez et validez les certificats à tout moment, depuis n'importe quel appareil.",
    feature4Title: "Plateforme de Confiance",
    feature4Text: "Conçu pour permettre aux organisations et aux participants de bâtir la confiance.",
    ctaTitle: "Commencez avec CertiLink Aujourd'hui",
    ctaSubtitle: "Rejoignez CertiLink et délivrez des certificats authentiques en toute confiance.",
    requestNow: "Demander un accès",
    checkingPermissions: "Vérification des permissions...",
    addParticipantTitle: "➕ Ajouter un Participant",
    participantWord: "Le participant",
    addedSuccessSuffix: "a été ajouté avec succès", // Pour construire: Le participant Name a été ajouté avec succès
    errorAddingParticipant: "Erreur lors de l'ajout du participant",
    serverError: "Erreur serveur. Veuillez réessayer.",
    nameLabel: "Nom *",
    namePlaceholder: "Entrer le nom du participant",
    emailLabel: "Email *",
    emailPlaceholder: "Entrer l'email du participant",
    clientLabel: "Client *",
    selectClientDefault: "-- Sélectionner un client --",
    autoAssignNote: "Ce participant sera automatiquement assigné à votre compte.",
    cancel: "Annuler",
    adding: "Ajout...",
    addParticipantButton: "Ajouter le Participant",
    loading: "Chargement...",
    fetchError: "Échec de la récupération des demandes.",
    confirmApprove: "Approuver l'accès pour",
    confirmReject: "Rejeter cette demande ?",
    adminRequestsTitle: "Gestion des Demandes d'Accès",
    thName: "Nom",
    thEmail: "Email",
    thOrg: "Organisation",
    thRole: "Rôle",
    thDate: "Date",
    thStatus: "Statut",
    thActions: "Actions",
    noRequests: "Aucune demande trouvée.",
    statusPending: "EN ATTENTE",
    statusApproved: "APPROUVÉ",
    statusRejected: "REJETÉ",
    details: "Détails",
    approve: "Approuver",
    reject: "Rejeter",
    completed: "Terminé",
    rejectedLabel: "Rejeté",
    requestDetails: "Détails de la demande",
    close: "Fermer",
    labelName: "Nom",
    labelEmail: "Email",
    labelPhone: "Téléphone",
    labelOrg: "Organisation",
    labelRole: "Rôle",
    labelReason: "Motif",
    labelDate: "Date",
    labelStatus: "Statut",
    bulkCreateTitle: "Générer Plusieurs Certificats Simultanément",
    bulkCreateDesc: "Générer des certificats via un fichier CSV",
    needFormat: "Besoin du format ?",
    downloadTemplateDesc: "Télécharger le modèle CSV pré-rempli.",
    downloadTemplateBtn: "Télécharger le Modèle",
    mainThemeLabel: "Thème Principal *",
    selectThemePlaceholder: "Sélectionner un thème",
    subThemeLabel: "Sous-thème *",
    selectSubThemePlaceholder: "Sélectionner un sous-thème",
    startDateLabel: "Date de Début *",
    csvFileLabel: "Fichier Participants (.csv) *",
    readyImport: "Prêt à être importé",
    dragDropCsv: "Cliquez ou glissez votre fichier CSV ici",
    csvFormatNote: "Format : nom, email, success",
    processing: "Traitement...",
    bulkCreateBtn: "Générer Plusieurs Certificats",
    errorFillFields: "Veuillez remplir tous les champs et ajouter un fichier.",
    importSuccess: "Importation réussie !",
    loadingThemes: "Chargement des thèmes...",
    failedFor: "Échec pour",
    createCertTitle: "Créer un Certificat",
    selectTheme: "Sélectionner un Thème",
    selectSubTheme: "Sélectionner un Sous-thème",
    successLabel: "Succès ?",
    createCertBtn: "Créer le Certificat",
    creating: "Création en cours...",
    certCreatedSuccess: "✅ Certificat créé avec succès !",
    certCreateError: "❌ Erreur lors de la création du certificat",
    networkError: "❌ Erreur réseau",
    loadingCertificates: "Chargement des certificats...",
    accessDenied: "Accès Refusé",
    goToLogin: "Aller à la connexion",
    certificatesOf: "Certificats de",
    thisParticipant: "ce participant",
    noCertFound: "Aucun certificat trouvé pour ce participant.",
    colId: "ID",
    colTheme: "Thème",
    colSubTheme: "Sous-thème",
    colStartDate: "Date de Début",
    colEndDate: "Date de Fin",
    colDuration: "Durée",
    colAction: "Action",
    days: "jours",
    downloadPdf: "Télécharger PDF",
    downloadFailed: "Échec du téléchargement",
    errorLabel: "Erreur",
    clientsManagement: "Gestion des Clients",
    loadingClients: "Chargement des clients...",
    loadClientsError: "Impossible de charger les clients.",
    updateFailed: "Échec de la mise à jour",
    colName: "Nom",
    colEmail: "Email",
    colParticipants: "Participants",
    colAccountStatus: "Statut du Compte",
    active: "Actif",
    inactive: "Inactif",
    confirmBlock: "Êtes-vous sûr de vouloir bloquer l'accès pour {name} ?",
    confirmActivate: "Êtes-vous sûr de vouloir activer l'accès pour {name} ?",
    dashboardTitle: "Tableau de Bord Analytique",
    dashboardSubtitle: "Vue d'ensemble des performances",
    kpiTotalCertifs: "Total Certificats",
    kpiTotalClients: "Total Clients",
    kpiActive: "Actifs",
    kpiInactive: "Inactifs",
    chartCreationFlow: "Flux de Création (7 Jours)",
    chartLabelGenerated: "Certificats Générés",
    chartSuccessRate: "Taux de Réussite",
    success: "Succès",
    failure: "Échec",
    chartTopClients: "Meilleurs Clients",
    certificates: "Certificats",
    noClient: "Aucun Client",
    chartThemeDist: "Distribution par Thèmes",
    noData: "Aucune Donnée",
    clientHealthTitle: "Santé de la Base Clients",
    clientHealthDesc: "Ratio clients actifs vs inactifs.",
    active: "Actif",
    inactive: "Inactif",
    systemStatus: "État du Système",
    systemOperational: "Tous les services sont opérationnels.",
    uptime: "Disponibilité",
    apiLatency: "Latence API",
    loadingParticipants: "Chargement des participants...",
    confirmDeleteParticipant: "Êtes-vous sûr de vouloir supprimer ce participant ?",
    participantDeletedSuccess: "Participant supprimé avec succès",
    participantsOf: "Participants de",
    allParticipants: "Tous les Participants",
    allParticipantsLow: "tous les participants",
    clientViewLabel: "Vue Client",
    clientViewDesc: "Affichage de vos participants uniquement",
    adminViewLabel: "Vue Admin",
    showing: "Affichage de",
    publicViewLabel: "Vue Publique",
    publicViewDesc: "Affichage de tous les participants",
    name: "Nom",
    email: "Email",
    client: "Client",
    actions: "Actions",
    createCertificate: "Créer Certificat",
    delete: "Supprimer",
    noParticipantsFound: "Aucun participant trouvé !",
    finalizeRegistration: "Finaliser l'inscription",
    completeSetupSecurely: "Terminez la configuration de votre compte en toute sécurité.",
    verifiedLocked: "Vérifié & Verrouillé",
    createPassword: "Créer un mot de passe",
    enterName: "Entrez votre nom",
    chooseStrongPassword: "Choisissez un mot de passe fort",
    creatingAccount: "Création du compte...",
    completeRegistration: "Terminer l'inscription",
    linkExpired: "Ce lien a expiré. Veuillez en demander un nouveau.",
    accountCreatedSuccess: "Compte créé avec succès ! Redirection...",
    registrationFailed: "L'inscription a échoué",
    serverConnectionError: "Erreur de connexion au serveur.",
    errorLabel: "Erreur",
    requestAccess: "Demander un accès",
    requestAccessDesc: "Remplissez ce formulaire pour demander la création d'un compte.",
    fullName: "Nom complet *",
    enterFullName: "Entrez votre nom complet",
    emailAddress: "Adresse Email *",
    enterEmail: "Entrez votre email",
    phoneNumber: "Numéro de téléphone *",
    organizationLabel: "Organisation *",
    companyNamePlaceholder: "Nom de l'entreprise",
    roleLabel: "Rôle *",
    selectRole: "Sélectionnez votre rôle",
    roleOwner: "Propriétaire / PDG",
    roleManager: "Manager",
    roleHR: "Ressources Humaines",
    roleIT: "IT / Sécurité",
    roleCompliance: "Conformité / Qualité",
    roleEmployee: "Employé",
    reasonLabel: "Raison de la demande *",
    reasonPlaceholder: "Pourquoi souhaitez-vous un accès ?",
    backToLogin: "Retour à la connexion",
    submitRequest: "Envoyer la demande",
    requestSentSuccess: "Demande envoyée avec succès ! Vous recevrez un email une fois que l'administrateur aura approuvé votre compte.",
    errorLabel: "Erreur",
    serverError: "Erreur serveur. Veuillez réessayer plus tard.",
    backToHome: "Retour à l'accueil",
    searchingCertificates: "Recherche des certificats...",
    noResultsFound: "Aucun résultat trouvé",
    trySearchingAgain: "Essayez de chercher avec des mots-clés différents.",
    verificationResultsTitle: "Résultats de Vérification",
    verified: "Vérifié",
    startDate: "Date de début",
    endDate: "Date de fin",
    duration: "Durée",
    days: "jours",
    expiration: "Expiration",
    unknown: "Inconnu",
    download: "Télécharger",
    singleCertVerifiedPrefix: "Le certificat est",
    allCertsVerifiedPrefix: "Tous les certificats sont",
    and: "et",
    authentic: "authentique",
    errorLabel: "Erreur",
    verifiedBy: "Vérifié par CertiLink",
    certificateId: "ID du Certificat",
    certifiesThat: "Ce certificat est attribué à :",
    mainTheme: "Thème Principal",
    specialization: "Spécialisation",
    requiredSkills: "Compétences Aquises",
    noPrerequisites: "Aucun prérequis spécifique n'est requis pour cette formation.",
    officialDocument: "Document Numérique Officiel",
    validCertificate: "Certificat Valide",
    notVerified: "Non Vérifié",
    invalidLink: "Lien invalide ou expiré.",
    backHome: "Retour à l'accueil",
    verifying: "Vérification",
    pageNotFound: "Page introuvable",
    pageNotFoundDesc: "Oups ! La page que vous cherchez semble s'être égarée dans le néant numérique.",
    backHome: "Retour à l'accueil",
    goBack: "Retour en arrière",
    errorReport: "Si vous pensez qu'il s'agit d'une erreur, veuillez contacter le support.",
    accessDenied: "Accès refusé",
    accessDeniedDesc: "Désolé, vous n'avez pas les permissions nécessaires pour voir cette page.",
    login: "Se connecter",
    loginButton: "Se connecter",
    loginTitle: "Connexion",
    signIn: "Connectez-vous pour accéder à votre compte",
    passwordLabel: "Mot de passe *",
    passwordPlaceholder: "Entrez votre mot de passe",
    typeLabel: "Type de compte *",
    adminRole: "Administrateur",
    clientRole: "Client",
    secretCodeLabel: "Code Secret *",
    secretCodePlaceholder: "Entrez le code secret",
    loggingIn: "Connexion en cours...",
    loginSuccess: "Connexion réussie !",
    selectModelLabel: "Sélectionnez un modèle *",
    selectModelLabelcb4c: "Modèle CB4C",
    selectModelLabelcertilink: "Modèle CertiLink",
    endDateLabel: "Date de fin *",
    modify: "Modifier",
    modifyDesc: "Modifier le participant",
    save: "Enregistrer",
    saving: "Enregistrement en cours...",
    dateModeLabel: "Mode de date *",
    dateRangeOption: "Plage de dates",
    dateDaysOption: "Nombre de jours",
    trainingDaysLabel: "Jours de formation *",
    addDayBtn: "Ajouter",
    noSelectedDaysHint: "Aucun jour sélectionné. Veuillez ajouter au moins un jour de formation.",
    derivedRangeLabel: "Période",
    durationLabel: "Durée",
    daysLabel: "jours",
    skills: {
      "Management de la Qualité": {
        "Exigences et principes de la norme ISO 9001:2015": [
          "Comprendre les principes de management de la qualité",
          "Comprendre le vocabulaire et les concepts clés de la norme ISO 9001:2015",
          "Comprendre & maîtriser les exigences de la norme ISO 9001:2015",
          "Appliquer les principes de management de la qualité dans un système conforme à l'ISO 9001:2015",
          "Elaborer des procédures et processus conformes aux exigences de la norme ISO 9001:2015",
          "Comprendre le principe du management des risques appliqué à la qualité",
          "Mettre en œuvre des actions d'amélioration continue au sein de l'organisation"
        ],
        "Auditeur Interne ISO 9001:2015": [
          "Consolidation des connaissances par rapport aux exigences de l’ISO 9001:2015",
          "S’exercer sur la planification des audits (Programme, Revue documentaire, Plan, Check-lists...)",
          "Compréhension des techniques d’audit et du comportement de l’auditeur",
          "Pratique de l’audit interne par rapport aux exigences de la norme ISO 9001:2015 & ISO 19011:2018",
          "S’exercer sur la formulation des constats d’audit"
        ],
        "Exigences et Audit Interne ISO 9001:2015": [
          "Comprendre les principes de management de la qualité",
          "Comprendre le vocabulaire et les concepts clés de la norme ISO 9001:2015",
          "Comprendre & maîtriser les exigences de la norme ISO 9001:2015",
          "Appliquer les principes de management de la qualité dans un système conforme à l'ISO 9001:2015",
          "Comprendre les techniques d’audit et du comportement de l’auditeur",
          "Pratiquer l’audit interne par rapport aux exigences de la norme ISO 9001:2015 & ISO 19011:2018",
          "S’exercer sur la formulation des constats d’audit"
        ],
        "Exigences ISO 9001:2015 & Auditeur Interne ISO 19011:2026": [
          "Comprendre les exigences de la norme ISO 9001:2015 et les principes d'un Système de Management de la Qualité.",
          "Maîtriser les principes et méthodes d'audit interne selon l'ISO 19011:2026",
          "Planifier et préparer un audit interne du Système de Management de la Qualité",
          "Évaluer la conformité aux exigences de la norme ISO 9001:2015",
          "Identifier les écarts, les non-conformités et les opportunités d'amélioration",
          "Rédiger des constats d'audit et contribuer à l'amélioration continue du système qualité."
        ],
        "Les techniques d'audit selon l'ISO 19011:2026": [
          "Comprendre les principes et les lignes directrices de l’audit selon l’ISO 19011:2026",
          "Acquérir les techniques d’audit et comprendre le comportement attendu de l’auditeur selon l’ISO 19011:2026",
          "Compréhension des tâches de l’auditeur interne",
          "Comprendre les rôles, responsabilités et tâches de l’auditeur interne",
          "Maîtriser les différentes étapes de réalisation d’un audit interne",
          "Appliquer les méthodes et techniques d’audit selon l’ISO 19011:2026",
          "Développer les compétences nécessaires pour conduire un audit interne",
          "Pratique de l’audit interne selon l'ISO 19011:2026"
        ]
      },
      "Management de l’Environnement": {
        "Exigences ISO 14001:2015": [
          "Comprendre les exigences et le vocabulaire de la norme ISO 14001:2015",
          "Connaître les principes du management environnemental et l'approche PDCA",
          "Identifier les aspects et impacts environnementaux significatifs pour l'organisation",
          "Appliquer les exigences de la norme pour structurer et améliorer un SME"
        ],
        "Auditeur Interne ISO 14001:2015": [
          "Consolidation des connaissances par rapport aux exigences de l’ISO 14001:2015",
          "S’exercer sur la planification des audits (Programme, Revue documentaire, Plan, Check-lists...)",
          "Compréhension des techniques d’audit et du comportement de l’auditeur",
          "Pratique de l’audit interne par rapport aux exigences de la norme ISO 14001:2015 & ISO 19011:2018",
          "S’exercer sur la formulation des constats d’audit"
        ],
        "Analyse du Cycle de Vie du Produit selon les normes ISO 14040V2006 & ISO 14044V2006": [
          "Comprendre les exigences des normes ISO 14040 et ISO 14044 ",
          "Réaliser une ACV complète appliquée à leur activité ",
          "Identifier les impacts environnementaux significatifs ",
          "Exploiter les résultats pour améliorer la performance environnementale",
          "Intégrer l’ACV dans une démarche ISO 14001 / stratégie ESG "
        ],
        "Analyse de cycle de vie dans le contexte de l'ISO 14040:2006/Amd 1:2020 et 14044:2006 Amd 2:2020": [
          "Comprendre les exigences des normes ISO 14040 et ISO 14044",
          "Réaliser une ACV complète appliquée à leur activité",
          "Identifier les impacts environnementaux significatifs",
          "Exploiter les résultats pour améliorer la performance environnementale Intégrer l’ACV dans une démarche ISO 14001 / stratégie ESG"
        ],
        "ISO 14001 – Intégration du Système de Management Environnemental": [
          "Comprendre les principes et les exigences de la norme ISO 14001:2015",
          "Maîtriser les éléments clés d’un Système de Management Environnemental (SME)",
          "Identifier les aspects environnementaux et évaluer leurs impacts associés",
          "Appliquer l’approche processus et l’approche par les risques environnementaux",
          "Mettre en œuvre les actions nécessaires à l’intégration d’un SME",
          "Contribuer à l’amélioration continue de la performance environnementale"
        ]
      },
      "Management de la Santé et Sécurité au Travail": {
        "Exigences ISO 45001:2018": [
          "Comprendre les exigences de l’ISO 45001:2018",
          "Comprendre les enjeux de la SST",
          "Comprendre le cadre réglementaire et légal en termes de SST",
          "Apprendre les notions d’identification des dangers, d’évaluation et de maîtrise des risques en matière de SST",
          "Apprendre les exigences de l’ISO 45001:2018 à travers des exercices pratiques"
        ],
        "Auditeur Interne ISO 45001:2018": [
          "Consolidation des connaissances par rapport aux exigences de la norme ISO 45001:2018",
          "Compréhension des tâches de l’auditeur interne",
          "Pratique de l’audit interne par rapport aux exigences de la norme ISO 45001:2018",
          "Acquérir et comprendre les techniques et le comportement de l’auditeur par rapport à la norme ISO 19011:2018"
        ],
        "Sécurité pour les correspondants sécurité": [
          "Comprendre le rôle et les responsabilités du correspondant sécurité",
          "Identifier les dangers et analyser les risques au poste de travail",
          "Réaliser des observations sécurité terrain efficaces",
          "Détecter et remonter les situations dangereuses et presque accidents",
          "Promouvoir la culture sécurité au sein de l'organisation et participer à l'amélioration des performances HSE."
        ],
        "Atmosphère Explosives – ATEX": [
          "Comprendre les principes de prévention des risques liés aux atmosphères explosives (ATEX)",
          "Identifier les zones ATEX et les sources potentielles d'inflammation",
          "Appliquer les mesures de prévention et de protection contre les explositions",
          "Évaluer les risques d'explosion liés aux installations et aux activités.",
          "Maîtriser les exigences réglementaires et les mesures de prévention applicables",
          "Appliquer les bonnes pratiques de sécurité lors des interventions en zones ATEX",
          "Contribuer à la prévention des risques d'explosion et à l'amélioration de la sécurité des installations"
        ],
        "Habilitation électrique": [
          "Opérer en sécurité sur tout ou partie d’un ouvrage en exploitation",
          "Apprendre la réglementation en matière selon la norme NF C 18-510",
          "Appliquer les consignes de sécurité liées aux consignations, aux interventions générales, aux travaux hors tension ou au voisinage effectué sur des ouvrages ou des installations électriques",
          "Identifier l'ensemble des risques et être capable de faire l'analyse des risques",
          "Prendre connaissance des différentes règles de sécurité concernant l’intervention dans un voisinage à risque électrique",
          "Se protéger contre les risques électriques"
        ],
        "LOTO & Permis de travail": [
          "Identifier les différentes sources d’énergies dangereuses",
          "Comprendre les risques liés aux interventions sur équipements",
          "Appliquer correctement les procédures LOTO",
          "Réaliser une consignation et déconsignation sécurisée",
          "Mettre en œuvre les permis de travail adaptés aux activités à risque",
          "Vérifier l’absence d’énergie avant intervention",
          "Utiliser correctement les dispositifs de verrouillage et d’étiquetage",
          "Prévenir les accidents liés aux redémarrages intempestifs",
          "Assurer la coordination des travaux de maintenance et sous-traitance"
        ]
      },
      "Management de l’Énergie": {
        "Exigences ISO 50001:2018": [
          "Comprendre les exigences de la norme pour une mise en œuvre pratique",
          "Comprendre et apprendre à travailler avec la famille des normes ISO 50001",
          "Maîtriser les notions de la revue énergétique",
          "Appliquer les exigences de la norme pour structurer et améliorer un SMEn"
        ],
        "Auditeur Interne ISO 50001:2018": [
          "Consolidation des connaissances par rapport aux exigences de l’ISO 50001:2018",
          "S’exercer sur la planification des audits internes (Programme, Revue documentaire, Plan, Check-lists...)",
          "Compréhension des techniques d’audit et du comportement de l’auditeur",
          "Pratique de l’audit interne par rapport aux exigences de la norme ISO 50001:2018",
          "S’exercer sur la formulation des constats d’audit"
        ],
        "Responsable management de l'énergie": [
          "Comprendre les principes du management de l'énergie",
          "Maîtriser les exigences de la norme ISO 50001",
          "Identifier les usages énergétiques significatifs et les opportunités d'amélioration",
          "Définir et piloter les plans d'action énergétiques",
          "Piloter un Système de Management de l'Énergie (SMÉ)",
          "Définir et mettre en œuvre des actions d'amélioration pour optimiser les consommations et les coûts énergétiques"
        ]
      },
      "Système de Management Intégré": {
        "Auditeur Interne SMI Energie et Environnement (ISO 50001 & ISO 14001)": [
          "La consolidation des connaissances par rapport aux exigences de l’ISO 5000:2018 & l'ISO 14001:2015",
          "La maîtrise des techniques d’audit et du comportement de l’auditeur",
          "La capacité de préparer une checklist d'audit complète et pertinente",
          "La rédaction des constats d’audit d'une manière claire et correcte"
        ],
        "Exigences d’un Système de Management Intégré : Qualité, Environnement et Sécurité/Santé au Travail ISO 9001 :2015 / ISO 14001 :2015 / ISO 45001 :2018": [
          "Comprendre les exigences des normes ISO 9001:2015, ISO 14001:2015 et ISO 45001:2018",
          "Interpréter les exigences d'un Système de Management Intégré (SMI)",
          "Maîtriser l'approche processus et l'approche par les risques",
          "Identifier les exigences clés pour mettre en œuvre un Système de Management Intégré",
          "Appliquer les exigences des normes au contexte de son organisation",
          "Contribuer à l'amélioration continue de la performance du Système de Management Intégré"
        ],
        "Auditeur Interne d’un Système de Management Intégré : Qualité, Environnement et Sécurité/Santé au Travail ISO 9001 :2015 / ISO 14001 :2015 / ISO 45001 :2018": [
          "Consolider les connaissances par rapport aux exigences des normes ISO 9001:2015, ISO 14001:2015 et ISO 45001:2018",
          "Comprendre les principes et les techniques de l'audit interne d'un Système de Management Intégré",
          "Comprendre des tâches de l’auditeur interne selon la norme ISO 19011:2026",
          "Planifier et préparer un audit interne selon les lignes directrices de l'ISO 19011:2026",
          "Comprendre les techniques et le comportement de l’auditeur par rapport aux normes ISO 9001:2015, ISO 14001:2015 et ISO 45001:2018",
          "Réaliser un audit interne des systèmes de management ISO 9001, ISO 14001 et ISO 45001",
          "Identifier les non-conformités, les écarts et les opportunités d'amélioration"
        ],
        "Exigences d’un Système de Management Intégré : Qualité, Environnement, Energie & et Sécurité/Santé au Travail ISO 9001:2015 / ISO 14001:2015 / ISO 50001:2018 / ISO 45001:2018": [
          "Comprendre les exigences des normes ISO 9001:2015, ISO 14001:2015,ISO 45001:2018 & ISO 50001:2018",
          "Interpréter les exigences d'un Système de Management Intégré (SMI)",
          "Maîtriser l'approche processus et l'approche par les risques",
          "Identifier les exigences clés pour mettre en œuvre un Système de Management Intégré",
          "Appliquer les exigences des normes au contexte de son organisation",
          "Contribuer à l'amélioration continue de la performance du Système de Management Intégré"
        ],
        "Auditeur Interne d’un Système de Management Intégré : Qualité, Environnement, Energie et Sécurité/Santé au Travail ISO 9001 :2015 / ISO 14001 :2015 / ISO 45001 :2018 / ISO 50001:2018": [
          "Consolider les connaissances par rapport aux exigences des normes ISO 9001:2015, ISO 14001:2015,ISO 45001:2018 & ISO 50001:2018",
          "Comprendre les principes et les techniques de l'audit interne d'un Système de Management Intégré",
          "Comprendre des tâches de l’auditeur interne selon la norme ISO 19011:2026",
          "Planifier et préparer un audit interne selon les lignes directrices de l'ISO 19011:2026",
          "Comprendre les techniques et le comportement de l’auditeur par rapport aux normes ISO 9001:2015, ISO 14001:2015,ISO 45001:2018 & ISO 50001:2018",
          "Réaliser un audit interne des systèmes de management ISO 9001:2015, ISO 14001:2015,ISO 45001:2018 & ISO 50001:2018",
          "Identifier les non-conformités, les écarts et les opportunités d'amélioration"
        ],
        "Auditeur Interne d’un Système de Management Intégré Environnement et Sécurité/Santé au Travail ISO 14001 :2015 / ISO 45001 :2018": [
          "Consolider les connaissances par rapport aux exigences des normes ISO 14001:2015 & ISO 45001:2018",
          "Comprendre les principes et les techniques de l'audit interne d'un Système de Management Intégré",
          "Comprendre des tâches de l’auditeur interne selon la norme ISO 19011:2026",
          "Planifier et préparer un audit interne selon les lignes directrices de l'ISO 19011:2026",
          "Comprendre les techniques et le comportement de l’auditeur par rapport aux normes ISO 14001:2015 & ISO 45001:2018",
          "Réaliser un audit interne des systèmes de management SO 14001:2015,ISO 45001:2018",
          "Identifier les non-conformités, les écarts et les opportunités d'amélioration"
        ]
      },
      "Management de la Sécurité de l’Information": {
        "Exigences et principes de la norme ISO 27001:2022": [
          "Comprendre les principes et les concepts du Système de Management de la Sécurité de l'Information (SMSI).",
          "Maîtriser les exigences de la norme ISO/IEC 27001:2022",
          "Interpréter les exigences de la norme dans un contexte organisationnel",
          "Identifier les exigences applicables pour la mise en œuvre d'un SMSI",
          "Contribuer à la mise en œuvre, au maintien et à l'amélioration continue d'un SMSI conforme à l'ISO/IEC 27001:2022"
        ],
        "Auditeur Interne ISO 27001:2022": [
          "Consolidation des connaissances par rapport aux exigences de l’ISO 27001 :2022",
          "Planifier un audit interne conformément aux exigences de l'ISO/IEC 27001:2022",
          "S’exercer sur la planification des audits (Programme, Revue documentaire, Plan, Check-lists...)",
          "Compréhension des techniques d’audit et du comportement de l’auditeur",
          "Pratique de l’audit interne par rapport aux exigences de la norme ISO 27001 :2022&ISO 19011 :2026",
          "Identifier les non-conformités, les écarts et les opportunités d'amélioration",
          "S’exercer sur la formulation des constats d’audit",
          "Maîtriser les exigences réglementaires applicables à la sécurité des systèmes d'information",
          "Identifier les obligations de conformité relatives aux systèmes d'information",
          "Planifier et préparer un audit des systèmes d'information conformément à la réglementation tunisienne",
          "Évaluer le niveau de conformité d'un système d'information aux exigences réglementaires",
          "Identifier les risques, vulnérabilités et non-conformités affectant la sécurité des systèmes d'information",
          "Analyser l'efficacité des mesures de sécurité mises en œuvre"
        ],
        "Audit des Systèmes de l’Information selon la Règlementation Tunisienne": [
          "Comprendre les exigences de la réglementation tunisienne applicables à l’audit des systèmes d’information.",
          "Maîtriser les principes, les méthodes et les étapes d’un audit des systèmes d’information.",
          "Évaluer la conformité d’un système d’information aux exigences réglementaires tunisiennes.",
          "Identifier les risques, les vulnérabilités et les écarts de conformité des systèmes d’information.",
          "Appliquer les techniques d’audit pour apprécier l’efficacité des mesures de sécurité mises en œuvre.",
          "Formuler des constats d’audit et des recommandations visant à renforcer la conformité et la sécurité des systèmes d’information."
        ]
      }
    }
  }
};

// 2. Création du contexte
const LanguageContext = createContext();

// 3. Le Provider qui entoure l'application
export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(localStorage.getItem("lang") || "en");

  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const toggleLanguage = () => {
    setLang((prev) => {
      const nextLang = prev === "en" ? "fr" : "en";
      document.documentElement.lang = nextLang;
      return nextLang;
    });
  };

  return (
    <LanguageContext.Provider value={{ lang, language: lang, toggleLanguage, t: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
};

// 4. Hook personnalisé pour utiliser la langue partout
export const useLanguage = () => useContext(LanguageContext);